/* Dice Goblin: Help me pick (assets/help-me-pick.js), round 17 (contract v17, section 9)
   <help-me-pick> (snippets/help-me-pick.liquid) takes anyone from "what should we play?" to three library games and a
   wildcard: one question a screen (play here or borrow, players, time, brain power, team up or not, the vibe), an
   optional swipe round, then the picks with Gobgob's reason line for each, where each one stands on the shelf and what
   to do next. Under the picks: fine-tune, different picks, start again, and keeping them (save, share, email).
   - In a dialog on the library pages and the booking page: any [data-help-me-pick] opens it (data-door, data-mode), and
     so does document.dispatchEvent(new CustomEvent('help-me-pick:open', { detail: { door, mode, players, date,
     bookingId, tables } })).
   - In the page itself on /pages/help-me-pick (sections/help-me-pick-page.liquid), which reads ?door=table&table=T14 (a
     table's QR code: play here, at that table), ?kiosk=1 (the shop's tablet: big type, no logging in, starts fresh
     after a minute without a touch, keeps nothing) and ?p=<id> (picks someone shared, read only).
   It talks to the Lair app itself (settings.lair_api, /apps/liar) with fetch, as lair-wishlist.js does, live and on
   Demo alike (contract v17, 9.1: the picks need the real library), and doesn't load lair-core.js. The answers, the
   session and its edit key are kept in sessionStorage, so a refresh (or logging in and coming back) keeps the picks.
   Every word comes from locales/en.default.json (help_me_pick) through Shopify's t filter, which escapes HTML
   (We&#39;re), so the words are decoded once as they're read and are plain text after that. */
(() => {
  'use strict';
  if (customElements.get('help-me-pick')) return;

  const decode = (s) => String(s ?? '').replace(/&(amp|lt|gt|quot|#39|#x27);/g, (m, e) => ({ amp: '&', lt: '<', gt: '>', quot: '"', '#39': "'", '#x27': "'" })[e]);
  const decodeAll = (x) => {
    if (typeof x === 'string') return decode(x);
    if (x && typeof x === 'object') return Object.fromEntries(Object.entries(x).map(([key, value]) => [key, decodeAll(value)]));
    return x;
  };
  const esc = (value) => String(value ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);
  const fill = (text, values = {}) => String(text ?? '').replace(/\[(\w+)\]/g, (_, key) => (values[key] ?? ''));
  const readJSON = (el) => {
    try {
      return el ? JSON.parse(el.textContent || 'null') : null;
    } catch {
      return null;
    }
  };
  /** FNV-1a: the same game always gets the same reason line, and the four cards rarely share one */
  const hash = (text) => {
    let h = 2166136261;
    for (const c of String(text)) {
      h ^= c.codePointAt(0);
      h = Math.imul(h, 16777619);
    }
    return h >>> 0;
  };
  const clamp = (n, low, high) => Math.min(high, Math.max(low, n));
  const int = (value) => Math.floor(Number(value)) || 0;
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  const STORE_KEY = 'dg-help-me-pick';
  const VIBES = ['chill', 'laughs', 'thinky', 'cutthroat'];
  const THEMES = ['fantasy', 'scifi', 'spooky', 'mystery', 'history', 'nature', 'cute', 'food', 'adventure', 'travel', 'heroes', 'abstract', 'words', 'funny', 'sports', 'science'];
  /** The roleplaying branch's worlds: the locale key, then the theme word the Lair matches on */
  const SETTINGS = [['fantasy', 'fantasy'], ['scifi', 'sci-fi'], ['horror', 'horror'], ['mystery', 'mystery'], ['superheroes', 'superheroes'], ['history', 'history'], ['any', 'any']];
  const STEPS = ['mode', 'players', 'time', 'brain', 'team', 'vibe', 'swipe'];
  const RPG_STEPS = ['mode', 'rpg-tried', 'rpg-players', 'rpg-setting', 'rpg-gm'];
  const DOORS = ['library', 'collection', 'booking', 'kiosk', 'table', 'page', 'my-lair', 'share'];
  const TIME_BUCKETS = [15, 20, 30, 45, 60, 90, 120, 180, 240];
  const MAX_LOVES = 5;

  /** sessionStorage, which can be missing or refuse (a private window, blocked site data): the quiz works without it */
  const storage = {
    read() {
      try {
        const data = JSON.parse(window.sessionStorage.getItem(STORE_KEY) || 'null');
        return data && data.v === 1 && data.step ? data : null;
      } catch {
        return null;
      }
    },
    write(state) {
      try {
        window.sessionStorage.setItem(STORE_KEY, JSON.stringify(state));
      } catch {
        /* nothing kept: a refresh starts again */
      }
    },
    clear() {
      try {
        window.sessionStorage.removeItem(STORE_KEY);
      } catch {
        /* fine */
      }
    },
  };

  /** One call to the Lair app. A network failure or a timeout (15 seconds) is status 0; the app's own refusals keep
      their status and its plain sentence ({ error }) */
  const request = async (base, path, { body, method, keepalive = false } = {}) => {
    const controller = 'AbortController' in window && !keepalive ? new AbortController() : null;
    const timer = controller ? window.setTimeout(() => controller.abort(), 15000) : 0;
    let response;
    try {
      response = await fetch(`${base}${path}`, {
        method: method || (body ? 'POST' : 'GET'),
        headers: { Accept: 'application/json', ...(body ? { 'Content-Type': 'application/json' } : {}) },
        body: body ? JSON.stringify(body) : undefined,
        credentials: 'same-origin',
        signal: controller ? controller.signal : undefined,
        keepalive,
      });
    } catch {
      throw Object.assign(new Error(''), { status: 0 });
    } finally {
      window.clearTimeout(timer);
    }
    let data = null;
    try {
      data = await response.json();
    } catch {
      data = null;
    }
    if (!response.ok) throw Object.assign(new Error((data && typeof data.error === 'string' && data.error) || ''), { status: response.status });
    if (!data || typeof data !== 'object') throw Object.assign(new Error(''), { status: 0 });
    return data;
  };

  /** The quiz on the page (its own page) wins, else the first one with a dialog: a page renders the snippet once, but
      two banners on one page would render it twice */
  const hostEl = () => document.querySelector('help-me-pick[data-inline]') || document.querySelector('help-me-pick');

  class HelpMePick extends HTMLElement {
    connectedCallback() {
      if (this.started) return;
      this.started = true;
      HelpMePick.count = (HelpMePick.count || 0) + 1;
      this.uid = `hmp${HelpMePick.count}`;
      this.w = decodeAll(readJSON(this.querySelector('script[data-hmp-words]')) || {});
      this.cfg = readJSON(this.querySelector('script[data-hmp-config]')) || {};
      this.api = String(this.cfg.api || '/apps/liar').replace(/\/+$/, '');
      this.inline = this.hasAttribute('data-inline');
      this.dialog = this.inline ? null : this.querySelector('dialog');
      this.root = this.querySelector('[data-hmp-root]');
      this.live = this.querySelector('[data-hmp-live]');
      this.icons = {};
      const icons = this.querySelector('template[data-hmp-icons]');
      if (icons) icons.content.querySelectorAll('[data-icon]').forEach((el) => { this.icons[el.dataset.icon] = el.innerHTML.trim(); });
      this.notes = {};
      this.queued = new Set(); // product ids waiting in their library queue (GET /me/queue, or added here)
      this.view = null;
      this.kiosk = false;
      this.shared = null;
      this.addEventListener('click', (event) => this.onClick(event));
      this.addEventListener('change', (event) => this.onChange(event));
      this.addEventListener('input', (event) => this.onInput(event));
      this.addEventListener('keydown', (event) => this.onKey(event));
      this.addEventListener('submit', (event) => this.onSubmit(event));
      this.addEventListener('toggle', (event) => this.onToggle(event), true);
      if (this.dialog) this.dialog.addEventListener('close', () => this.onClosed());
      this.ready = true;
      if (this.inline) this.startInline();
    }

    /* ---------- words, icons and who's here ---------- */

    /** A word by its path under help_me_pick (players.question), with [placeholders] filled when values are given */
    t(path, values) {
      const text = String(path).split('.').reduce((o, key) => (o && typeof o === 'object' ? o[key] : undefined), this.w);
      if (typeof text !== 'string') return '';
      return values ? fill(text, values) : text;
    }

    /** The variants of a line ("a | b | c"), one chosen by the seed. Gobgob speaks of themself in a few variants: once
        one card has, the others pick a variant without them (this.gobSaid, reset for each set of picks) */
    variant(path, seed) {
      const list = this.t(path).split('|').map((x) => x.trim()).filter(Boolean);
      if (!list.length) return '';
      const name = this.t('gobgob');
      let i = hash(seed) % list.length;
      if (name && this.gobSaid && list[i].includes(name)) {
        const other = list.findIndex((x, n) => n >= i && !x.includes(name));
        const first = list.findIndex((x) => !x.includes(name));
        i = other >= 0 ? other : first >= 0 ? first : i;
      }
      if (name && list[i].includes(name)) this.gobSaid = true;
      return list[i];
    }

    icon(name) {
      return this.icons[name] || '';
    }

    /** Logged in (never on the kiosk, which is for everyone) */
    get customer() {
      return this.kiosk ? null : this.cfg.customer || null;
    }

    /** Whether they have a library plan: the Lair's answer (GET /me/queue's plan) once it's in, else their tags' hint.
        'unknown' still offers members' actions, and the Lair's refusal (Join the library) comes back with its own words */
    get plan() {
      const c = this.customer;
      if (!c) return 'guest';
      if (this.lairPlan !== undefined) return this.lairPlan ? 'member' : 'none';
      if (c.member || int(c.libraryGames) > 0) return 'member';
      return 'unknown';
    }

    call(path, options) {
      return request(this.api, path, options);
    }

    /* ---------- opening, closing, starting ---------- */

    /** A context from a door: the table's QR code and the booking page know it's play here */
    contextFrom(detail = {}) {
      const door = DOORS.includes(detail.door) ? detail.door : this.inline ? 'page' : 'library';
      const table = /^[A-Za-z0-9-]{1,12}$/.test(String(detail.table || '')) ? String(detail.table).toUpperCase() : null;
      const bookingId = detail.bookingId ? String(detail.bookingId).slice(0, 64) : null;
      let mode = ['play', 'borrow'].includes(detail.mode) ? detail.mode : null;
      if (door === 'table' || door === 'booking' || bookingId) mode = 'play';
      return {
        door: table && door === 'table' ? 'table' : door,
        table: door === 'table' ? table : null,
        bookingId,
        date: /^\d{4}-\d{2}-\d{2}$/.test(String(detail.date || '')) ? detail.date : null,
        tables: int(detail.tables) || null,
        players: clamp(int(detail.players), 0, 8) || null,
        kids: detail.kids === true || int(detail.youngest) > 0,
        youngest: clamp(int(detail.youngest), 0, 17) || null,
        mode,
      };
    }

    /** Carry on where they were (a refresh, logging in and coming back, opening it again) unless this door is a
        different table or booking, or fixes a different way to play */
    sameContext(stored, ctx) {
      if (!stored) return false;
      const was = stored.context || {};
      if ((was.bookingId || ctx.bookingId) && was.bookingId !== ctx.bookingId) return false;
      if ((was.table || ctx.table) && was.table !== ctx.table) return false;
      if (ctx.mode && stored.mode && stored.mode !== ctx.mode) return false;
      return true;
    }

    fresh(ctx) {
      return {
        v: 1,
        step: null,
        history: [],
        mode: ctx.mode || null,
        modeFixed: Boolean(ctx.mode),
        players: ctx.players || null,
        kids: Boolean(ctx.kids),
        youngest: ctx.youngest && ctx.youngest >= 2 ? ctx.youngest : 6,
        time: null,
        brain: null,
        team: null,
        vibes: [],
        swipes: {},
        swipeAt: 0,
        candidates: null,
        swipeKey: '',
        swipeSkipped: false,
        skipSent: false,
        rpg: { tried: null, players: null, setting: null, gm: null },
        filters: { themes: [], loves: [], lightReading: false, colourBlind: false, easyPieces: false, hideHad: false },
        context: { door: ctx.door, table: ctx.table, bookingId: ctx.bookingId, date: ctx.date, tables: ctx.tables },
        session: null,
        result: null,
      };
    }

    begin(ctx) {
      this.state = this.fresh(ctx);
      this.state.step = this.stepsFor()[0];
      this.notes = {};
      this.view = null;
      this.persist();
    }

    persist() {
      if (this.kiosk || this.shared || !this.state) return;
      storage.write(this.state);
    }

    open(detail = {}, opener = null) {
      if (this.shared) this.leaveShared();
      const ctx = this.contextFrom(detail);
      const stored = this.kiosk ? null : this.state || storage.read();
      if (stored && this.sameContext(stored, ctx)) this.state = stored;
      else this.begin(ctx);
      if (this.inline) {
        this.render();
        this.focusTop();
        return;
      }
      if (!this.dialog) return;
      this.opener = opener && opener !== document.body ? opener : document.activeElement;
      if (!this.dialog.open) {
        this.dialog.showModal();
        document.body.classList.add('has-open-dialog');
      }
      this.render();
      this.focusTop();
      this.lookAgain();
    }

    close() {
      if (this.dialog && this.dialog.open) this.dialog.close();
    }

    /** Closed (the close button, Esc or a tap on the shade): the page scrolls again and focus goes back to what opened it */
    onClosed() {
      window.clearTimeout(this.tuneTimer);
      if (!document.querySelector('dialog[open]')) document.body.classList.remove('has-open-dialog');
      const opener = this.opener;
      this.opener = null;
      if (opener && opener.isConnected && typeof opener.focus === 'function') opener.focus({ preventScroll: true });
    }

    /** Its own page: shared picks, the kiosk, a table's QR code, or carry on from before */
    startInline() {
      const query = new URLSearchParams(window.location.search);
      this.kiosk = query.get('kiosk') === '1';
      if (this.kiosk) {
        document.documentElement.classList.add('hmp-kiosk-page');
        this.classList.add('hmp--kiosk');
      }
      const shared = query.get('p');
      if (shared && /^[A-Za-z0-9]{4,40}$/.test(shared) && !this.kiosk) {
        this.loadShared(shared);
        return;
      }
      const ctx = this.contextFrom(this.kiosk ? { door: 'kiosk' } : { door: query.get('door') || 'page', table: query.get('table'), mode: query.get('mode') });
      const stored = this.kiosk ? null : storage.read();
      if (stored && this.sameContext(stored, ctx)) this.state = stored;
      else this.begin(ctx);
      this.render();
      if (this.kiosk) this.startIdle();
      this.lookAgain();
    }

    /** Back with picks from before: the shelf may have changed since, so the Lair is asked again, quietly */
    async lookAgain() {
      const s = this.state;
      if (!s || s.step !== 'results' || !s.session || !s.result || this.view) return;
      try {
        const data = await this.call(`/pick/${encodeURIComponent(s.session.id)}`);
        if (this.state !== s || !data || !Array.isArray(data.picks)) return;
        const before = [...(s.result.picks || []), s.result.wildcard].filter(Boolean);
        const keepWhy = (card) => {
          if (!card || Array.isArray(card.why)) return card;
          const old = before.find((c) => String(c.productId) === String(card.productId));
          return old && Array.isArray(old.why) ? { ...card, why: old.why } : card;
        };
        s.result = { ...s.result, picks: data.picks.filter(Boolean).map(keepWhy), wildcard: keepWhy(data.wildcard || null), playerType: data.playerType || s.result.playerType };
        this.persist();
        this.renderPicks();
      } catch {
        /* the picks as they were */
      }
    }

    /* ---------- the kiosk: start fresh after a minute without a touch ---------- */

    startIdle() {
      const seconds = clamp(int(this.cfg.kioskSeconds) || 60, 15, 600);
      this.idleMs = seconds * 1000;
      this.warnMs = Math.min(10000, Math.floor(this.idleMs / 3));
      const touched = (event) => this.touched(event);
      ['pointerdown', 'wheel', 'touchstart', 'input'].forEach((type) => document.addEventListener(type, touched, { passive: true, capture: true }));
      document.addEventListener('keydown', touched, { capture: true });
      this.touched();
    }

    /** A touch: the clock starts again. "Still there?" stays until it's tapped (it covers the screen, so the tap that
        answers it can't land on the quiz under it) */
    touched(event) {
      window.clearTimeout(this.idleTimer);
      window.clearInterval(this.countdown);
      const still = this.querySelector('[data-hmp-still]');
      if (still && event && !/^(pointer|touch)/.test(event.type)) {
        // a key (or the wheel) answers it: that key does nothing else
        if (event.type === 'keydown') event.preventDefault();
        still.remove();
        this.querySelector('[data-hmp-focus]')?.focus({ preventScroll: true });
      }
      this.idleTimer = window.setTimeout(() => this.stillThere(), this.idleMs - this.warnMs);
    }

    stay() {
      this.querySelector('[data-hmp-still]')?.remove();
      this.touched();
      this.querySelector('[data-hmp-focus]')?.focus({ preventScroll: true });
    }

    /** Nothing answered yet: nothing to start again from. Otherwise "Still there?" with a countdown, then start fresh */
    stillThere() {
      const s = this.state;
      const pristine = s && s.step === this.stepsFor()[0] && !s.history.length && !this.view;
      if (pristine) {
        this.idleTimer = window.setTimeout(() => this.stillThere(), this.idleMs);
        return;
      }
      this.querySelector('[data-hmp-still]')?.remove();
      let left = Math.round(this.warnMs / 1000);
      const box = document.createElement('div');
      box.className = 'hmp-still';
      box.setAttribute('data-hmp-still', '');
      box.setAttribute('role', 'alertdialog');
      box.setAttribute('aria-labelledby', `${this.uid}-still`);
      box.setAttribute('aria-describedby', `${this.uid}-still-text`);
      box.setAttribute('aria-modal', 'true');
      box.innerHTML = `<div class="hmp-still__panel">
          <p class="hmp-still__title" id="${this.uid}-still">${esc(this.t('kiosk.still'))}</p>
          <p class="hmp-still__text" id="${this.uid}-still-text">${esc(this.t('kiosk.still_text', { seconds: left }))}</p>
          <button class="button button--mana" type="button" data-hmp-stay>${esc(this.t('kiosk.stay'))}</button>
        </div>`;
      this.append(box);
      box.querySelector('button').focus({ preventScroll: true });
      this.countdown = window.setInterval(() => {
        left -= 1;
        const text = box.querySelector('.hmp-still__text');
        if (text) text.textContent = this.t('kiosk.still_text', { seconds: Math.max(0, left) });
        if (left <= 0) {
          window.clearInterval(this.countdown);
          box.remove();
          this.startOver({ quiet: true });
        }
      }, 1000);
    }

    /* ---------- the steps ---------- */

    /** The questions for this way through: the roleplaying branch has its own; a door that knows how they'll play
        skips the first */
    stepsFor(s = this.state) {
      const list = s && s.mode === 'rpg' ? RPG_STEPS.slice() : STEPS.slice();
      if (s && s.modeFixed) list.shift();
      return list;
    }

    progress() {
      const s = this.state;
      const steps = this.stepsFor();
      if (s.step === 'results') return { done: true, at: steps.length, total: steps.length };
      return { done: false, at: Math.max(0, steps.indexOf(s.step)) + 1, total: steps.length };
    }

    go(step, { back = false } = {}) {
      const s = this.state;
      if (!back && s.step && s.step !== step) s.history.push(s.step);
      s.step = step;
      this.view = null;
      this.persist();
      this.render();
      this.focusTop();
      if (step === 'results') this.fetchPicks();
      else if (step === 'swipe') this.loadSwipes();
    }

    next() {
      const steps = this.stepsFor();
      const at = steps.indexOf(this.state.step);
      this.go(at >= 0 && at < steps.length - 1 ? steps[at + 1] : 'results');
    }

    back() {
      const s = this.state;
      this.ask = (this.ask || 0) + 1; // anything still loading is no longer wanted
      let prev = s.history.pop();
      if (prev === 'swipe' && Array.isArray(s.candidates) && !s.candidates.length) prev = s.history.pop() || prev;
      if (!prev) return;
      const list = s.candidates || [];
      if (prev === 'swipe' && list.length && (s.swipeAt || 0) >= list.length) {
        s.swipeAt = list.length - 1;
        delete s.swipes[list[s.swipeAt].productId];
      }
      if (prev === 'swipe') s.swipeSkipped = false;
      this.go(prev, { back: true });
    }

    answer(key, value) {
      const s = this.state;
      if (key === 'mode') {
        s.mode = value;
        s.candidates = null;
      } else if (key === 'players') s.players = clamp(int(value), 1, 8);
      else if (key === 'time' || key === 'brain' || key === 'team') s[key] = value;
      else if (key === 'rpg-tried') s.rpg.tried = value;
      else if (key === 'rpg-players') s.rpg.players = clamp(int(value), 1, 8);
      else if (key === 'rpg-setting') s.rpg.setting = value;
      else if (key === 'rpg-gm') s.rpg.gm = value;
      this.next();
    }

    /** Focus the screen's heading, so a screen reader reads the new question and Tab starts from the top */
    focusTop() {
      window.requestAnimationFrame(() => {
        const body = this.querySelector('[data-hmp-body]');
        if (body && this.dialog) body.scrollTop = 0;
        const heading = this.querySelector('[data-hmp-focus]');
        if (heading) heading.focus({ preventScroll: true });
        if (this.inline && heading) {
          const top = heading.getBoundingClientRect().top;
          // what's stuck to the top of the screen once it scrolls: the site's header, and under it the quiz's own bar
          // (Question 3 of 7), which sticks at the header's foot (its computed top), so the question lands below both
          const bar = this.querySelector('.hmp__bar');
          const barStyle = bar ? getComputedStyle(bar) : null;
          const clear = barStyle && barStyle.position === 'sticky' ? (parseFloat(barStyle.top) || 0) + bar.offsetHeight : 0;
          if (top < clear + 8 || top > window.innerHeight * 0.6) {
            window.scrollTo({ top: Math.max(0, window.scrollY + top - clear - 16), behavior: reduceMotion.matches ? 'auto' : 'smooth' });
          }
        }
      });
    }

    /** Say something to screen readers, once, even when it's the same words as last time */
    say(text) {
      if (!this.live || !text) return;
      this.live.textContent = '';
      window.setTimeout(() => {
        this.live.textContent = text;
      }, 60);
    }

    /* ---------- drawing ---------- */

    render() {
      if (!this.root) return;
      if (this.shared) {
        this.renderShared();
        return;
      }
      if (!this.state) return;
      const s = this.state;
      let body;
      if (this.view === 'loading') body = this.waitHTML(this.loadingText || this.t('loading'));
      else if (this.view === 'error') body = this.errorHTML(this.errorText);
      else if (s.step === 'results') body = this.resultsHTML();
      else if (s.step === 'swipe') body = this.swipeStepHTML();
      else body = this.stepHTML(s.step);
      this.root.innerHTML = `${this.barHTML()}<div class="hmp__body" data-hmp-body>${body}</div>`;
      this.root.dataset.step = s.step;
      if (s.step === 'results' && !this.view) this.afterResults();
      if (s.step === 'swipe' && !this.view) this.bindDrag(this.querySelector('[data-hmp-card]'));
    }

    barHTML() {
      const s = this.state;
      const p = this.progress();
      const canBack = s.history.length > 0;
      // "Your picks are ready" only once there are picks to see (not over a problem, the wait or an empty result)
      const ready = Boolean(s.result && (s.result.picks || []).length && !this.view);
      const text = p.done ? (ready ? this.t('progress_done') : '') : this.t('progress', { step: p.at, total: p.total });
      const share = p.done ? 1 : (p.at - 1) / p.total;
      return `<div class="hmp__bar">
          ${canBack
            ? `<button class="icon-button hmp__back" type="button" data-hmp-back>${this.icon('back')}<span class="visually-hidden">${esc(this.t('back'))}</span></button>`
            : '<span class="hmp__back-gap" aria-hidden="true"></span>'}
          <div class="hmp__progress">
            <p class="hmp__count">${text ? esc(text) : '&nbsp;'}</p>
            <span class="hmp__track" aria-hidden="true"><span class="hmp__fill" style="--hmp-done: ${share.toFixed(3)}"></span></span>
          </div>
          ${this.dialog
            ? `<button class="icon-button hmp__close" type="button" data-hmp-close>${this.icon('close')}<span class="visually-hidden">${esc(this.t('close'))}</span></button>`
            : '<span class="hmp__back-gap" aria-hidden="true"></span>'}
        </div>`;
    }

    contextHTML() {
      const c = this.state.context || {};
      let text = '';
      if (c.door === 'table' && c.table) text = this.t('context_table', { table: c.table });
      else if (c.bookingId) text = this.t('context_booking');
      return text ? `<p class="hmp-context">${this.icon('table')}<span>${esc(text)}</span></p>` : '';
    }

    question(text, hint = '') {
      return `<h2 class="hmp-step__q" id="${this.uid}-q" tabindex="-1" data-hmp-focus>${esc(text)}</h2>${
        hint ? `<p class="hmp-step__hint" id="${this.uid}-hint">${esc(hint)}</p>` : ''}`;
    }

    /** One answer: a big button with its words and a quieter line under them. Tapping it answers and moves on. */
    option(key, value, label, hint, { icon = '', current = null } = {}) {
      return `<button class="hmp-option" type="button" data-hmp-answer="${esc(key)}" data-value="${esc(value)}" aria-pressed="${current === value ? 'true' : 'false'}">
          ${icon ? `<span class="hmp-option__icon" aria-hidden="true">${this.icon(icon)}</span>` : ''}
          <span class="hmp-option__text"><span class="hmp-option__label">${esc(label)}</span>${hint ? `<span class="hmp-option__hint">${esc(hint)}</span>` : ''}</span>
        </button>`;
    }

    counts(key, current) {
      const buttons = [];
      for (let n = 1; n <= 8; n += 1) {
        const name = n === 8 ? this.t('players.most') : this.t(n === 1 ? 'players.count_one' : 'players.count_other', { n });
        const face = n === 8 ? this.t('players.most_short') : String(n);
        buttons.push(`<button class="hmp-count" type="button" data-hmp-answer="${key}" data-value="${n}" aria-pressed="${current === n ? 'true' : 'false'}" aria-label="${esc(name)}"><span aria-hidden="true">${esc(face)}</span></button>`);
      }
      return `<div class="hmp-counts" role="group" aria-labelledby="${this.uid}-q">${buttons.join('')}</div>`;
    }

    stepHTML(step) {
      const s = this.state;
      const wrap = (inner, extra = '') => `<section class="hmp-step hmp-step--${esc(step)}${extra}" aria-labelledby="${this.uid}-q">${inner}</section>`;
      if (step === 'mode') {
        return wrap(`${this.contextHTML()}${this.question(this.t('mode.question'))}
          <div class="hmp-options">
            ${this.option('mode', 'play', this.t('mode.play'), this.t('mode.play_hint'), { icon: 'table', current: s.mode })}
            ${this.option('mode', 'borrow', this.t('mode.borrow'), this.t('mode.borrow_hint'), { icon: 'book', current: s.mode })}
          </div>
          <p class="hmp-step__aside"><button class="text-link hmp-link" type="button" data-hmp-rpg>${this.icon('d20')}<span>${esc(this.t('mode.rpg'))}</span></button></p>`);
      }
      if (step === 'players') {
        const age = clamp(int(s.youngest) || 6, 2, 17);
        return wrap(`${s.history.length ? '' : this.contextHTML()}${this.question(this.t('players.question'))}
          <div class="hmp-kids">
            <label class="hmp-switch">
              <input class="hmp-switch__input" type="checkbox" role="switch" data-hmp-kids aria-describedby="${this.uid}-kids-hint"${s.kids ? ' checked' : ''}>
              <span class="hmp-switch__text"><span class="hmp-switch__label">${esc(this.t('players.kids'))}</span><span class="hmp-switch__hint" id="${this.uid}-kids-hint">${esc(this.t('players.kids_hint'))}</span></span>
            </label>
            <div class="hmp-age" data-hmp-age-box${s.kids ? '' : ' hidden'}>
              <label class="hmp-age__label" for="${this.uid}-age">${esc(this.t('players.youngest'))}</label>
              <div class="stepper">
                <button class="stepper__button" type="button" data-hmp-age="-1" aria-controls="${this.uid}-age"${age <= 2 ? ' disabled' : ''}>${this.icon('minus')}<span class="visually-hidden">${esc(this.t('players.younger'))}</span></button>
                <input class="stepper__value" id="${this.uid}-age" type="number" inputmode="numeric" min="2" max="17" step="1" value="${age}" data-hmp-age-input>
                <button class="stepper__button" type="button" data-hmp-age="1" aria-controls="${this.uid}-age"${age >= 17 ? ' disabled' : ''}>${this.icon('plus')}<span class="visually-hidden">${esc(this.t('players.older'))}</span></button>
              </div>
            </div>
          </div>
          ${this.counts('players', s.players)}`);
      }
      if (step === 'time') {
        return wrap(`${this.question(this.t('time.question'))}<div class="hmp-options">${['short', 'medium', 'long', 'epic']
          .map((v) => this.option('time', v, this.t(`time.${v}`), this.t(`time.${v}_hint`), { icon: 'clock', current: s.time })).join('')}</div>`);
      }
      if (step === 'brain') {
        return wrap(`${this.question(this.t('brain.question'))}<div class="hmp-options">${['easy', 'some', 'big']
          .map((v) => this.option('brain', v, this.t(`brain.${v}`), this.t(`brain.${v}_hint`), { icon: 'gauge', current: s.brain })).join('')}</div>`);
      }
      if (step === 'team') {
        const icons = { coop: 'players', competitive: 'crown', either: 'shapes' };
        return wrap(`${this.question(this.t('team.question'))}<div class="hmp-options">${['coop', 'competitive', 'either']
          .map((v) => this.option('team', v, this.t(`team.${v}`), this.t(`team.${v}_hint`), { icon: icons[v], current: s.team })).join('')}</div>`);
      }
      if (step === 'vibe') {
        const chosen = s.vibes.length > 0;
        return wrap(`${this.question(this.t('vibe.question'), this.t('vibe.hint'))}
          <div class="hmp-options hmp-options--grid" role="group" aria-labelledby="${this.uid}-q" aria-describedby="${this.uid}-hint">${VIBES.map((v) => `
            <button class="hmp-option hmp-option--toggle" type="button" data-hmp-vibe="${v}" aria-pressed="${s.vibes.includes(v) ? 'true' : 'false'}">
              <span class="hmp-option__text"><span class="hmp-option__label">${esc(this.t(`vibe.${v}`))}</span><span class="hmp-option__hint">${esc(this.t(`vibe.${v}_hint`))}</span></span>
              <span class="hmp-option__tick" aria-hidden="true">${this.icon('check')}</span>
            </button>`).join('')}
          </div>
          <div class="hmp-step__foot">
            <button class="button button--mana hmp-step__next" type="button" data-hmp-next${chosen ? '' : ' hidden'}>${esc(this.t('vibe.next'))}</button>
            <button class="button button--ghost hmp-step__next" type="button" data-hmp-any>${esc(this.t('vibe.any'))}</button>
          </div>`);
      }
      if (step === 'rpg-tried') {
        return wrap(`${this.question(this.t('rpg.tried.question'))}<div class="hmp-options">${['never', 'some', 'lots']
          .map((v) => this.option('rpg-tried', v, this.t(`rpg.tried.${v}`), this.t(`rpg.tried.${v}_hint`), { icon: 'd20', current: s.rpg.tried })).join('')}</div>`);
      }
      if (step === 'rpg-players') return wrap(`${this.question(this.t('rpg.players.question'))}${this.counts('rpg-players', s.rpg.players)}`);
      if (step === 'rpg-setting') {
        return wrap(`${this.question(this.t('rpg.setting.question'))}<div class="hmp-options hmp-options--grid">${SETTINGS
          .map(([key, value]) => this.option('rpg-setting', value, this.t(`rpg.setting.${key}`), '', { current: s.rpg.setting })).join('')}</div>`);
      }
      if (step === 'rpg-gm') {
        return wrap(`${this.question(this.t('rpg.gm.question'))}<div class="hmp-options">
            ${this.option('rpg-gm', 'lair', this.t('rpg.gm.lair'), this.t('rpg.gm.lair_hint'), { icon: 'table', current: s.rpg.gm })}
            ${this.option('rpg-gm', 'own', this.t('rpg.gm.own'), this.t('rpg.gm.own_hint'), { icon: 'players', current: s.rpg.gm })}
          </div>`);
      }
      return '';
    }

    waitHTML(text) {
      return `<div class="hmp-wait" role="status"><span class="hmp-wait__die" aria-hidden="true">${this.icon('d20')}</span><p class="hmp-wait__text" tabindex="-1" data-hmp-focus>${esc(text)}</p></div>`;
    }

    /** A problem that stops them: one plain sentence and Try again, never a blank screen */
    errorHTML(text) {
      return `<div class="hmp-error" role="alert">
          <p class="hmp-error__text" tabindex="-1" data-hmp-focus>${esc(text || this.t('error'))}</p>
          <button class="button button--mana" type="button" data-hmp-retry>${this.icon('refresh')}<span>${esc(this.t('retry'))}</span></button>
        </div>`;
    }

    /** Problems from the Lair: its own plain sentence for a refusal (busy, gone), ours for a network failure */
    problem(error) {
      const status = Number(error && error.status) || 0;
      if (status >= 400 && status < 500 && error.message) return error.message;
      return this.t('error');
    }

    showError(error, retry) {
      this.view = 'error';
      this.errorText = this.problem(error);
      this.retry = retry;
      this.render();
      this.focusTop();
      this.say(this.errorText);
    }

    /* ---------- the swipe round ---------- */

    /** What the swipe boxes depend on: change any of these (Back, a different answer) and they're asked for again */
    swipeBody() {
      const s = this.state;
      const body = { mode: s.mode, players: s.players || undefined, kids: Boolean(s.kids), time: s.time || undefined, context: this.contextBody() };
      if (s.kids) body.youngest = clamp(int(s.youngest) || 6, 2, 17);
      if (s.session) body.sessionId = s.session.id;
      return body;
    }

    async loadSwipes() {
      const s = this.state;
      const body = this.swipeBody();
      const key = JSON.stringify({ ...body, sessionId: undefined });
      this.checkTaste();
      if (s.candidates && s.swipeKey === key) return;
      const ask = (this.ask = (this.ask || 0) + 1);
      this.view = 'loading';
      this.loadingText = this.t('loading_swipes');
      this.render();
      try {
        const data = await this.call('/pick/swipes', { body });
        if (ask !== this.ask || this.state !== s || s.step !== 'swipe') return;
        s.candidates = (Array.isArray(data.candidates) ? data.candidates : []).filter((c) => c && c.productId).slice(0, 12);
        s.swipeKey = key;
        s.swipes = {};
        s.swipeAt = 0;
        this.view = null;
        this.persist();
        if (!s.candidates.length) {
          // nothing to show them: straight on to the picks
          s.history.push('swipe');
          s.step = 'results';
          this.persist();
          this.render();
          this.fetchPicks();
          return;
        }
        this.render();
        this.focusTop();
      } catch (error) {
        if (ask !== this.ask || this.state !== s) return;
        this.showError(error, () => this.loadSwipes());
      }
    }

    /** A logged-in member with enough taste saved gets "Use what you liked last time" (GET /me/picks: taste.has) */
    async checkTaste() {
      if (!this.customer || this.tasteAsked) return;
      this.tasteAsked = true;
      try {
        const data = await this.call('/me/picks');
        this.tasteHas = Boolean(data && data.taste && data.taste.has);
        if (this.tasteHas && this.state && this.state.step === 'swipe' && !this.view) {
          const slot = this.querySelector('[data-hmp-taste-slot]');
          if (slot) slot.innerHTML = this.tasteButton();
        }
      } catch {
        this.tasteHas = false;
      }
    }

    tasteButton() {
      return this.tasteHas ? `<button class="button button--ghost hmp-swipe__taste" type="button" data-hmp-taste>${esc(this.t('swipe.taste'))}</button>` : '';
    }

    mini(c) {
      const facts = [];
      const p = c.players || {};
      if (p.min) facts.push(`${this.icon('players')}<span>${esc(this.playersText(p))}</span>`);
      const tm = c.time || {};
      if (tm.min || tm.max) facts.push(`${this.icon('clock')}<span>${esc(this.timeText(tm))}</span>`);
      return facts.length ? `<ul class="stats hmp-swipe__stats" role="list">${facts.map((f) => `<li class="stats__item">${f}</li>`).join('')}</ul>` : '';
    }

    swipeStepHTML() {
      const s = this.state;
      const list = s.candidates || [];
      const at = clamp(s.swipeAt || 0, 0, Math.max(0, list.length - 1));
      const c = list[at];
      if (!c) return this.waitHTML(this.t('loading_swipes'));
      const next = list[at + 1];
      const picture = (x, eager) => (x.image
        ? `<img class="hmp-swipe__img" src="${esc(this.sized(x.image, 480))}" srcset="${esc(this.srcset(x.image))}" sizes="(min-width: 750px) 22rem, 70vw" width="480" height="480" alt="" ${eager ? 'loading="eager"' : 'loading="lazy"'} decoding="async" draggable="false">`
        : `<span class="hmp-swipe__noimg" aria-hidden="true">${this.icon('d20')}</span>`);
      return `<section class="hmp-step hmp-step--swipe" aria-labelledby="${this.uid}-q">
          ${this.question(this.t('swipe.question'), this.t('swipe.hint'))}
          <div class="hmp-swipe" data-hmp-swipe>
            <div class="hmp-swipe__deck">
              ${next ? `<div class="hmp-swipe__card hmp-swipe__card--under" aria-hidden="true"><div class="hmp-swipe__media">${picture(next, true)}</div></div>` : ''}
              <div class="hmp-swipe__card" data-hmp-card data-id="${esc(c.productId)}" tabindex="0" role="group"
                aria-labelledby="${this.uid}-card-title" aria-describedby="${this.uid}-card-text ${this.uid}-keys">
                <div class="hmp-swipe__media">${picture(c, true)}
                  <span class="hmp-swipe__stamp hmp-swipe__stamp--like" aria-hidden="true">${esc(this.t('swipe.like'))}</span>
                  <span class="hmp-swipe__stamp hmp-swipe__stamp--nope" aria-hidden="true">${esc(this.t('swipe.nope'))}</span>
                </div>
                <div class="hmp-swipe__text">
                  <p class="hmp-swipe__count">${esc(this.t('swipe.count', { n: at + 1, total: list.length }))}</p>
                  <h3 class="hmp-swipe__title" id="${this.uid}-card-title">${esc(c.title)}</h3>
                  <p class="hmp-swipe__summary" id="${this.uid}-card-text">${esc(c.summary || '')}</p>
                  ${this.mini(c)}
                </div>
              </div>
            </div>
            <p class="visually-hidden" id="${this.uid}-keys">${esc(this.t('swipe.keys'))}</p>
            <div class="hmp-swipe__buttons" role="group" aria-labelledby="${this.uid}-card-title">
              <button class="hmp-swipe__button hmp-swipe__button--nope" type="button" data-hmp-swipe-answer="nope">${this.icon('close')}<span>${esc(this.t('swipe.nope'))}</span></button>
              <button class="hmp-swipe__button hmp-swipe__button--skip" type="button" data-hmp-swipe-answer="skip"><span>${esc(this.t('swipe.skip'))}</span></button>
              <button class="hmp-swipe__button hmp-swipe__button--like" type="button" data-hmp-swipe-answer="like">${this.icon('heart')}<span>${esc(this.t('swipe.like'))}</span></button>
            </div>
          </div>
          <div class="hmp-step__foot hmp-swipe__foot">
            <button class="button button--ghost" type="button" data-hmp-just-show>${esc(this.t('swipe.done'))}</button>
            <span data-hmp-taste-slot>${this.tasteButton()}</span>
          </div>
        </section>`;
    }

    /** One box answered: remembered, read out, then the next box (or the picks after the last) */
    swipe(value, { from = 'button' } = {}) {
      const s = this.state;
      const list = s.candidates || [];
      const c = list[s.swipeAt || 0];
      if (!c || this.swiping) return;
      s.swipes[c.productId] = value;
      s.swipeAt = (s.swipeAt || 0) + 1;
      this.persist();
      const said = this.t(value === 'like' ? 'swipe.liked' : value === 'nope' ? 'swipe.noped' : 'swipe.skipped', { title: c.title });
      const next = list[s.swipeAt];
      const done = () => {
        this.swiping = false;
        if (!next) {
          this.say(said);
          this.next();
          return;
        }
        this.render();
        this.say(`${said} ${this.t('swipe.next_up', { title: next.title, n: s.swipeAt + 1, total: list.length })}`);
        // a keyboard or a button keeps its place, so the next box is one more press away
        if (from === 'key') this.querySelector('[data-hmp-card]')?.focus({ preventScroll: true });
        else if (from === 'button') this.querySelector(`[data-hmp-swipe-answer="${value}"]`)?.focus({ preventScroll: true });
      };
      const card = this.querySelector('[data-hmp-card]');
      if (!card || reduceMotion.matches || value === 'skip') {
        done();
        return;
      }
      // the box flies off the way it was sent
      this.swiping = true;
      card.classList.add(value === 'like' ? 'is-liked' : 'is-noped');
      card.style.transition = 'transform 0.22s ease-in, opacity 0.22s ease-in';
      card.style.transform = `translateX(${value === 'like' ? 120 : -120}%) rotate(${value === 'like' ? 14 : -14}deg)`;
      card.style.opacity = '0';
      window.setTimeout(done, 200);
    }

    /** Back in the swipe round takes back the last box first */
    unswipe() {
      const s = this.state;
      if (!s.swipeAt) return false;
      s.swipeAt -= 1;
      const c = (s.candidates || [])[s.swipeAt];
      if (c) delete s.swipes[c.productId];
      this.persist();
      this.render();
      this.querySelector('[data-hmp-card]')?.focus({ preventScroll: true });
      return true;
    }

    /**
     * Dragging the box: past a quarter of its width, or flicked, it's sent; less and it springs back. The box follows the
     * finger from the first few pixels. It takes every touch on it (touch-action: none, so the browser never grabs a
     * sideways thumb arc as a scroll), and a mostly up-and-down drag on it scrolls the page by hand instead.
     */
    bindDrag(card) {
      if (!card || card.dataset.dragBound) return;
      card.dataset.dragBound = '1';
      let start = null;
      const move = (event) => {
        if (!start || event.pointerId !== start.id) return;
        const dx = event.clientX - start.x;
        const dy = event.clientY - start.y;
        if (start.scrolling) {
          window.scrollBy(0, start.lastY - event.clientY);
          start.lastY = event.clientY;
          return;
        }
        if (!start.dragging) {
          // a finger's first few pixels say which way it's going; a thumb's arc starts a little up or down, so only a
          // clearly up-and-down move scrolls
          if (Math.max(Math.abs(dx), Math.abs(dy)) < 10) return;
          if (Math.abs(dy) > Math.abs(dx) * 1.8) {
            // up or down: a finger scrolls the page; a mouse just isn't dragging (yet)
            if (event.pointerType !== 'mouse') {
              start.scrolling = true;
              start.lastY = event.clientY;
              window.scrollBy(0, -dy);
            }
            return;
          }
          start.dragging = true;
          // a touch is captured to the box already; capturing it again would lose it and end the drag
          if (!card.hasPointerCapture?.(event.pointerId)) card.setPointerCapture?.(event.pointerId);
          card.classList.add('is-dragging');
        }
        start.dx = dx;
        start.trail.push({ t: event.timeStamp, x: event.clientX });
        if (start.trail.length > 8) start.trail.shift();
        card.style.transform = `translateX(${dx}px) rotate(${dx / 18}deg)`;
        card.style.setProperty('--hmp-lean', String(clamp(dx / (start.w * 0.25), -1, 1)));
      };
      const end = (event) => {
        if (!start || event.pointerId !== start.id) return;
        const { dx = 0, dragging, w, trail } = start;
        start = null;
        card.classList.remove('is-dragging');
        if (!dragging) return;
        // how fast it was going as it was let go (pixels a millisecond, over about the last 120ms): a flick sends it too
        const last = trail[trail.length - 1];
        const from = trail.find((p) => last.t - p.t <= 120) || trail[0];
        const speed = last.t > from.t ? (last.x - from.x) / (last.t - from.t) : 0;
        const flicked = event.type === 'pointerup' && Math.abs(speed) > 0.35 && Math.abs(dx) > 24 && Math.sign(speed) === Math.sign(dx);
        if (Math.abs(dx) > w * 0.25 || flicked) this.swipe(dx > 0 ? 'like' : 'nope', { from: 'drag' });
        else {
          card.style.transform = '';
          card.style.removeProperty('--hmp-lean');
        }
      };
      card.addEventListener('pointerdown', (event) => {
        if (event.button !== 0 || this.swiping) return;
        start = {
          id: event.pointerId, x: event.clientX, y: event.clientY, w: card.getBoundingClientRect().width || 300, dx: 0, dragging: false, scrolling: false,
          trail: [{ t: event.timeStamp, x: event.clientX }],
        };
      });
      card.addEventListener('pointermove', move);
      card.addEventListener('pointerup', end);
      card.addEventListener('pointercancel', end);
      // (not lostpointercapture: on a touchscreen it fired as the drag began, which ended every swipe about 20px in)
    }

    /* ---------- asking for the picks ---------- */

    contextBody() {
      const c = this.state.context || {};
      return { door: c.door || 'page', table: c.table || null, bookingId: c.bookingId || null, date: c.date || null };
    }

    /** POST /pick's body (contract v17, 2.1). Questions not asked are left out, and the Lair treats them as "not asked" */
    pickBody() {
      const s = this.state;
      const f = s.filters;
      const body = { mode: s.mode || 'play', context: this.contextBody(), useTaste: Boolean(this.customer) };
      if (s.mode === 'rpg') {
        const r = s.rpg;
        body.players = r.players || undefined;
        body.rpg = { tried: r.tried || 'never', players: r.players || 4, setting: r.setting || 'any', gm: r.gm || 'lair' };
        return body;
      }
      Object.assign(body, {
        players: s.players || undefined,
        kids: Boolean(s.kids),
        time: s.time || undefined,
        brain: s.brain || undefined,
        team: s.team || undefined,
        vibes: s.vibes.slice(),
        swipes: { ...s.swipes },
        rpg: null,
        filters: {
          themes: f.themes.slice(),
          loves: f.loves.map((x) => (x.productId ? { productId: String(x.productId) } : { known: x.title })),
          lightReading: Boolean(f.lightReading),
          colourBlind: Boolean(f.colourBlind),
          easyPieces: Boolean(f.easyPieces),
          hideHad: Boolean(f.hideHad && this.customer),
        },
      });
      if (s.kids) body.youngest = clamp(int(s.youngest) || 6, 2, 17);
      return body;
    }

    /** The picks: the first time (a loading screen), "Show me different picks" (again) or a fine-tune (the picks change
        where they are, so focus stays in the fine-tune controls) */
    async fetchPicks({ again = false, tune = false, fresh = false } = {}) {
      const s = this.state;
      const ask = (this.ask = (this.ask || 0) + 1);
      const body = this.pickBody();
      if (s.session && s.session.editKey && !fresh) {
        body.sessionId = s.session.id;
        body.editKey = s.session.editKey;
      }
      if (again) body.again = true;
      if (tune) this.busyPicks(true);
      else {
        this.view = 'loading';
        this.loadingText = this.t(again ? 'updating' : 'loading');
        this.render();
        this.focusTop();
      }
      try {
        const data = await this.call('/pick', { body });
        if (ask !== this.ask || this.state !== s) return;
        if (data.session && data.session.id) {
          s.session = { id: String(data.session.id), editKey: String(data.session.editKey || (s.session && s.session.editKey) || '') };
        }
        s.result = {
          mode: data.mode || s.mode,
          picks: Array.isArray(data.picks) ? data.picks.filter(Boolean) : [],
          wildcard: data.wildcard || null,
          playerType: data.playerType || null,
          fitCount: Number(data.fitCount) || 0,
          relaxed: Array.isArray(data.relaxed) ? data.relaxed : [],
        };
        this.notes = {};
        this.persist();
        if (s.swipeSkipped && !s.skipSent) {
          s.skipSent = true;
          this.persist();
          this.track('swipe-skip');
        }
        if (tune) {
          this.busyPicks(false);
          this.renderPicks();
          this.say(`${this.t('updated')} ${this.announceText()}`);
          return;
        }
        this.track('view');
        this.view = null;
        this.render();
        this.focusTop();
        this.say(this.announceText());
      } catch (error) {
        if (ask !== this.ask || this.state !== s) return;
        // a session that's gone (180 days) or isn't theirs any more: the same answers make a new one
        if (body.sessionId && (error.status === 403 || error.status === 404)) {
          s.session = null;
          this.fetchPicks({ again: false, tune, fresh: true });
          return;
        }
        if (tune) {
          this.busyPicks(false);
          this.tuneProblem(this.problem(error));
          return;
        }
        this.showError(error, () => this.fetchPicks({ again }));
      }
    }

    busyPicks(on) {
      const list = this.querySelector('[data-hmp-picks]');
      if (list) list.toggleAttribute('aria-busy', on);
      const status = this.querySelector('[data-hmp-tune-status]');
      if (status) {
        status.textContent = on ? this.t('updating') : '';
        status.classList.remove('is-problem');
      }
    }

    tuneProblem(text) {
      const status = this.querySelector('[data-hmp-tune-status]');
      if (!status) return;
      status.classList.add('is-problem');
      status.innerHTML = `<span>${esc(text)}</span> <button class="text-link" type="button" data-hmp-tune-retry>${esc(this.t('retry'))}</button>`;
      this.say(text);
    }

    /** A change in fine-tune: the picks follow a moment after the last one */
    tune() {
      this.persist();
      window.clearTimeout(this.tuneTimer);
      const status = this.querySelector('[data-hmp-tune-status]');
      if (status) {
        status.classList.remove('is-problem');
        status.textContent = this.t('updating');
      }
      this.tuneTimer = window.setTimeout(() => this.fetchPicks({ tune: true }), 700);
    }

    announceText() {
      const r = (this.shared || this.state).result || {};
      const picks = r.picks || [];
      if (!picks.length) return this.t('results.none');
      let text = this.t('results.announce', { titles: this.list(picks.map((p) => p.title)) });
      if (r.wildcard) text += ` ${this.t('results.announce_wildcard', { title: r.wildcard.title })}`;
      return text;
    }

    /** "A, B and C" */
    list(items) {
      const and = this.t('results.and');
      if (items.length < 2) return items.join('');
      return `${items.slice(0, -1).join(', ')} ${and} ${items[items.length - 1]}`;
    }

    /** Pick tracking: fire and forget (contract v17, 3.7), still sent when the tap leaves the page */
    track(action, productId = null) {
      const session = (this.shared && this.shared.session) || (this.state && this.state.session);
      if (!session || !session.id) return;
      const body = { action };
      if (productId) body.productId = String(productId);
      if (session.editKey) body.editKey = session.editKey;
      this.call(`/pick/${encodeURIComponent(session.id)}/event`, { body, keepalive: true }).catch(() => {});
    }

    /* ---------- facts in words ---------- */

    playersText(p = {}) {
      const min = int(p.min);
      const max = int(p.max);
      if (!min) return '';
      if (max >= 99 || max >= 20) return this.t('card.players_plus', { n: min });
      if (!max || max === min) return min === 1 ? this.t('card.players_one') : this.t('card.players_same', { n: min });
      return this.t('card.players', { min, max });
    }

    timeText(tm = {}) {
      const min = int(tm.min);
      const max = int(tm.max);
      if (!min && !max) return '';
      if (!max || min === max) return this.t('card.time_same', { n: min || max });
      if (!min) return this.t('card.time_same', { n: max });
      return this.t('card.time', { min, max });
    }

    /** A number as a word for Gobgob ("four"), up to twelve */
    word(n) {
      const words = this.t('reasons.numbers').split(',').map((x) => x.trim());
      const i = int(n);
      return i >= 1 && i <= words.length ? words[i - 1] : String(n);
    }

    /** About how long, in words: the bucket nearest the middle of the game's range */
    aboutTime(min, max) {
      const mid = ((int(min) || int(max)) + (int(max) || int(min))) / 2;
      if (!mid) return '';
      let best = TIME_BUCKETS[0];
      for (const b of TIME_BUCKETS) if (Math.abs(b - mid) < Math.abs(best - mid)) best = b;
      return this.t(`reasons.times.m${best}`);
    }

    /** One reason's words, as the lead (a sentence's start) or the tail (after ", and") */
    reasonText(r, part, seed) {
      if (!r || !r.code) return '';
      const key = String(r.code).replace(/-/g, '_');
      const n = int(r.n);
      let path = `reasons.${part}.${key}`;
      const values = {};
      if (key === 'best_at' || key === 'players') {
        if (n === 1) path += '_one';
        values.n = this.word(n);
      } else if (key === 'time') {
        values.time = this.aboutTime(r.min, r.max);
        if (!values.time) return '';
      } else if (key === 'brain') {
        if (!['easy', 'some', 'big'].includes(r.value)) return '';
        path = `reasons.${part}.brain_${r.value}`;
      } else if (key === 'vibe') {
        if (!VIBES.includes(r.vibe)) return '';
        path = `reasons.${part}.vibe_${r.vibe}`;
      } else if (key === 'like') {
        if (!r.title) return '';
        values.title = r.title;
      } else if (key === 'theme') {
        const theme = this.t(`reasons.themes.${String(r.group || '').replace(/-/g, '')}`);
        if (!theme) return '';
        values.theme = theme;
      } else if (key === 'kids') {
        if (!int(r.age)) return '';
        values.age = int(r.age);
      } else if (key === 'rated') {
        if (!int(r.ups)) return '';
        values.ups = int(r.ups);
      }
      const line = this.variant(path, `${seed}:${part}`);
      return line ? fill(line, values) : '';
    }

    /** Gobgob's one line for a pick: its strongest reason, then ", and" the next one (contract v17, 9.2). The variant
        is chosen by a hash of the game's id, so a game always reads the same and the cards rarely repeat each other. */
    reasonLine(card) {
      const seed = String(card.productId || card.title || '');
      const why = Array.isArray(card.why) ? card.why : [];
      let lead = '';
      let rest = why;
      for (let i = 0; i < why.length && !lead; i += 1) {
        lead = this.reasonText(why[i], 'lead', seed);
        rest = why.slice(i + 1);
      }
      if (!lead) return this.variant('reasons.fallback', seed) + this.t('reasons.end');
      let tail = '';
      for (const r of rest) {
        if (r.code === why[0].code) continue;
        tail = this.reasonText(r, 'tail', seed);
        if (tail) break;
      }
      // a line that ends in its own question or exclamation keeps it
      const stop = /[.!?]$/.test(lead) ? '' : this.t('reasons.end');
      return tail ? `${lead}${this.t('reasons.join')} ${tail}${this.t('reasons.end')}` : `${lead}${stop}`;
    }

    /** Where it's shelved, in words (contract v17, 9.1): "DGL34-M011: 3–4 players, middle shelves, 11th from the left".
        RPG books are just the code. */
    shelfWords(card) {
      const code = String(card.shelfCode || '').trim();
      if (!code) return '';
      const shelf = card.shelf || {};
      const match = /^(DGL(?:12|34|56|7\+|F|RPG))-([SML])?(\d+)$/i.exec(code);
      const section = String(shelf.section || (match && match[1]) || '').toUpperCase();
      const size = String(shelf.size || (match && match[2]) || '').toUpperCase();
      const number = int(shelf.number) || (match ? int(match[3]) : 0);
      if (section === 'DGLRPG' || !section || !number) return this.t('shelf.words_rpg', { code });
      const sectionWords = this.t(`shelf.${section.toLowerCase().replace('+', '')}`) || section;
      const values = { code, section: sectionWords, nth: this.ordinal(number) };
      if (!['S', 'M', 'L'].includes(size)) return this.t('shelf.words_nosize', values);
      return this.t('shelf.words', { ...values, size: this.t(`shelf.size_${size.toLowerCase()}`) });
    }

    ordinal(n) {
      const s = this.t('shelf.suffixes').split(',').map((x) => x.trim());
      const v = n % 100;
      const suffix = (v >= 11 && v <= 13) ? s[0] : s[n % 10] || s[0];
      return `${n}${suffix || ''}`;
    }

    /** A time the Lair gives (ms) in Lair time: a midnight is the end of the day before ("midnight, Thu 8 Oct") */
    when(ms) {
      const at = new Date(Number(ms));
      if (!Number(ms) || Number.isNaN(at.getTime())) return '';
      const tz = this.cfg.tz || 'Pacific/Auckland';
      const parts = (d, o) => new Intl.DateTimeFormat('en-NZ', { timeZone: tz, ...o }).formatToParts(d).reduce((acc, p) => ({ ...acc, [p.type]: p.value }), {});
      const t = parts(at, { hour: 'numeric', minute: '2-digit', hourCycle: 'h23' });
      const day = (d) => {
        const p = parts(d, { weekday: 'short', day: 'numeric', month: 'short' });
        return `${p.weekday} ${p.day} ${p.month}`;
      };
      if (int(t.hour) === 0 && int(t.minute) === 0) return this.t('status.midnight', { day: day(new Date(at.getTime() - 60000)) });
      const h = int(t.hour);
      const time = `${h % 12 || 12}${int(t.minute) ? `:${t.minute}` : ''}${h < 12 ? 'am' : 'pm'}`;
      return this.t('status.day_time', { day: day(at), time });
    }

    /** Where it stands right now (contract v17, 3.3's availability): the words and a dot colour */
    standing(card) {
      const a = card.availability || {};
      if (a.atHome && a.atHome.id) return { tone: 'mine', text: this.t('status.home') };
      if (a.mine && a.mine.id) return { tone: 'mine', text: this.t('status.mine', { when: this.when(a.mine.until) }) };
      const available = int(a.available);
      if (available > 0) return { tone: 'free', free: true, text: available > 1 ? this.t('status.free_other', { count: available }) : this.t('status.free_one') };
      if (Number(a.nextFree) > 0) return { tone: 'held', text: this.t('status.reserved', { when: this.when(a.nextFree) }) };
      if (int(a.held) > 0) return { tone: 'held', text: this.t('status.reserved_now') };
      if (int(a.out) > 0) return { tone: 'out', text: this.t('status.out') };
      return { tone: 'out', text: this.t('status.none') };
    }

    /** Shopify's CDN sizes a picture with width=; anything else is left as it is */
    sized(url, width) {
      const src = String(url || '');
      if (!/cdn\.shopify\.com|\/cdn\/shop\//.test(src)) return src;
      try {
        const u = new URL(src, window.location.href);
        u.searchParams.set('width', String(width));
        return u.href;
      } catch {
        return src;
      }
    }

    srcset(url) {
      const src = String(url || '');
      if (!/cdn\.shopify\.com|\/cdn\/shop\//.test(src)) return '';
      return [240, 360, 480, 720].map((w) => `${this.sized(src, w)} ${w}w`).join(', ');
    }

    pageUrl(card) {
      return card.handle ? `/products/${encodeURIComponent(card.handle)}` : '';
    }

    videoLink(card) {
      if (card.video) return { href: card.video, text: this.t('actions.video') };
      const q = this.t('actions.video_query', { title: card.title });
      return { href: `https://www.youtube.com/results?search_query=${encodeURIComponent(q)}`, text: this.t('actions.video_search') };
    }

    money(cents) {
      const n = Number(cents) / 100;
      if (!Number.isFinite(n)) return '';
      return `$${n % 1 ? n.toFixed(2) : n.toFixed(0)}`;
    }

    /* ---------- where things go ---------- */

    page(name) {
      const pages = this.cfg.pages || {};
      return String(pages[name] || '');
    }

    /** Logging in comes back here: the quiz's own page as it is, or this page with ?hmp=open, which opens the quiz again
        on the picks (they're kept in sessionStorage) */
    loginUrl() {
      let back = `${window.location.pathname}${window.location.search}`;
      if (!this.inline) {
        const u = new URL(window.location.href);
        u.searchParams.set('hmp', 'open');
        back = `${u.pathname}${u.search}`;
      }
      return `${this.page('login') || '/customer_authentication/login'}?return_to=${encodeURIComponent(back)}`;
    }

    shareUrl(id) {
      const path = this.page('quiz') || '/pages/help-me-pick';
      return `${window.location.origin}${path}?p=${encodeURIComponent(id)}`;
    }

    bookUrl(card) {
      const u = new URL(this.page('book') || '/pages/book-a-table', window.location.origin);
      u.searchParams.set('game', String(card.productId));
      const session = (this.shared && this.shared.session) || (this.state && this.state.session);
      if (session && session.id) u.searchParams.set('pick', session.id);
      return `${u.pathname}${u.search}`;
    }

    /* ---------- the picks ---------- */

    result() {
      return (this.shared || this.state).result || null;
    }

    modeOf() {
      const r = this.result();
      return (r && r.mode) || (this.shared ? this.shared.mode : this.state.mode) || 'play';
    }

    resultsHTML() {
      const s = this.state;
      const r = s.result;
      if (!r) return this.waitHTML(this.t('loading'));
      const picks = r.picks || [];
      const none = !picks.length;
      return `<section class="hmp-results" aria-labelledby="${this.uid}-results">
          <h2 class="hmp-results__heading" id="${this.uid}-results" tabindex="-1" data-hmp-focus>${esc(this.t('results.heading'))}</h2>
          ${this.contextHTML()}
          <div data-hmp-picks-box>${this.picksHTML()}</div>
          ${none ? '' : this.tuneHTML()}
          <div class="hmp-results__again">
            ${none ? '' : `<button class="button button--mana" type="button" data-hmp-again>${this.icon('refresh')}<span>${esc(this.t('results.again'))}</span></button>`}
            <button class="button button--ghost" type="button" data-hmp-start-again>${esc(this.t('results.start_again'))}</button>
          </div>
          ${none ? '' : this.keepHTML()}
        </section>`;
    }

    /** What changes when the picks change: the stretched line, the player type and the cards */
    picksHTML() {
      const r = this.result();
      if (!r) return '';
      const picks = r.picks || [];
      if (!picks.length) {
        return `<div class="hmp-none"><span class="hmp-none__die" aria-hidden="true">${this.icon('d20')}</span><p class="hmp-none__text">${esc(this.t('results.none'))}</p></div>`;
      }
      this.gobSaid = false;
      const cards = picks.map((card, i) => `<li class="hmp-picks__item">${this.cardHTML(card, { index: i })}</li>`);
      if (r.wildcard) cards.push(`<li class="hmp-picks__item hmp-picks__item--wild">${this.cardHTML(r.wildcard, { wild: true, index: picks.length })}</li>`);
      return `${this.relaxedHTML(r.relaxed)}${this.typeHTML(r.playerType)}
        <ol class="hmp-picks" role="list" aria-label="${esc(this.t('results.picks_label'))}" data-hmp-picks>${cards.join('')}</ol>`;
    }

    renderPicks() {
      const box = this.querySelector('[data-hmp-picks-box]');
      if (!box) {
        this.render();
        return;
      }
      box.innerHTML = this.picksHTML();
      this.afterResults();
    }

    relaxedHTML(relaxed) {
      const list = (Array.isArray(relaxed) ? relaxed : []).map((x) => this.t(`results.relaxed_${x}`)).filter(Boolean);
      if (!list.length) return '';
      return `<p class="hmp-relaxed">${this.icon('info')}<span>${esc(this.t('results.relaxed', { what: this.list(list) }))}</span></p>`;
    }

    typeHTML(type) {
      const known = ['party', 'thinker', 'team', 'competitor', 'adventurer', 'chill'];
      if (!known.includes(type)) return '';
      const canShare = !this.kiosk && !this.shared;
      const marks = { party: 'sparkle', thinker: 'shapes', team: 'players', competitor: 'crown', adventurer: 'sword', chill: 'heart' };
      return `<section class="hmp-type hmp-type--${esc(type)}" aria-labelledby="${this.uid}-type">
          <span class="hmp-type__mark" aria-hidden="true">${this.icon(marks[type])}</span>
          <p class="hmp-type__label">${esc(this.t(this.shared ? 'type.label_shared' : 'type.label'))}</p>
          <h3 class="hmp-type__name" id="${this.uid}-type">${esc(this.t(`type.${type}`))}</h3>
          <p class="hmp-type__line">${esc(this.t(`type.${type}_line`))}</p>
          <p class="hmp-type__site">${esc(this.t('type.site', { shop: this.cfg.shopName || '' }))}</p>
          ${canShare ? `<button class="button button--small button--ink hmp-type__share" type="button" data-hmp-share-type>${this.icon('external')}<span>${esc(this.t('type.share'))}</span></button>` : ''}
        </section>`;
    }

    statsHTML(card) {
      const items = [];
      const p = card.players || {};
      const players = this.playersText(p);
      if (players) {
        const best = (Array.isArray(p.best) ? p.best : []).map(int).filter(Boolean);
        const text = best.length ? this.t('card.best', { players, n: best.join(` ${this.t('card.or')} `) }) : players;
        items.push(['players', text]);
      }
      const time = this.timeText(card.time || {});
      if (time) items.push(['clock', time]);
      if (['easy', 'some', 'big'].includes(card.brain)) items.push(['gauge', this.t(`card.brain_${card.brain}`)]);
      if (['coop', 'competitive', 'team', 'solo'].includes(card.mode)) items.push([card.mode === 'coop' ? 'players' : 'crown', this.t(`card.mode_${card.mode}`)]);
      if (int(card.age)) items.push(['age', this.t('card.age', { age: int(card.age) })]);
      if (!items.length) return '';
      return `<ul class="stats hmp-pick__stats" role="list" aria-label="${esc(this.t('card.facts', { title: card.title }))}">${items
        .map(([icon, text]) => `<li class="stats__item">${this.icon(icon)}<span>${esc(text)}</span></li>`).join('')}</ul>`;
    }

    /** Whether to flag a big-box game: play mode only, and not when the booking already has two tables or more */
    flagDouble(card) {
      if (card.table !== 'double' || this.modeOf() !== 'play') return false;
      const c = (this.state && this.state.context) || {};
      return !(c.bookingId && int(c.tables) >= 2);
    }

    cardHTML(card, { wild = false, index = 0 } = {}) {
      const id = String(card.productId || '');
      const mode = this.modeOf();
      const url = this.pageUrl(card);
      const img = card.image
        ? `<img class="hmp-pick__img" src="${esc(this.sized(card.image, 480))}" srcset="${esc(this.srcset(card.image))}" sizes="(min-width: 990px) 16rem, (min-width: 600px) 12rem, 38vw" width="480" height="480" alt="" loading="${index < 2 ? 'eager' : 'lazy'}" decoding="async">`
        : `<span class="hmp-pick__noimg" aria-hidden="true">${this.icon('d20')}</span>`;
      const badges = [];
      if (card.staffPick) badges.push(`<span class="badge hmp-badge hmp-badge--staff">${esc(this.t('card.staff_pick'))}</span>`);
      if (card.isNew) badges.push(`<span class="badge badge--new">${esc(this.t('card.new'))}</span>`);
      const stand = mode === 'rpg' && !card.variantId ? null : this.standing(card);
      const notes = [];
      if (this.flagDouble(card)) notes.push(`<p class="hmp-pick__note">${this.icon('table')}<span>${esc(this.t('card.double'))}</span></p>`);
      if (card.inStoreOnly && mode === 'borrow') notes.push(`<p class="hmp-pick__note">${this.icon('lock')}<span>${esc(this.t('card.in_store'))}</span></p>`);
      if (card.missingPieces) {
        notes.push(`<div class="hmp-pick__note hmp-pick__note--missing" role="note">${this.icon('alert')}<span><strong>${esc(this.t('card.missing'))}</strong>${
          card.missingNote ? ` ${esc(this.t('card.missing_what'))} ${esc(card.missingNote)}` : ''}</span></div>`);
      }
      const addOns = (Array.isArray(card.addOns) ? card.addOns : []).filter((a) => a && a.title);
      const addOnsHTML = addOns.length
        ? `<p class="hmp-pick__addons"><span>${esc(this.t('card.add_ons'))}</span> ${addOns.map((a) => (a.handle && !this.kiosk
          ? `<a href="/products/${encodeURIComponent(a.handle)}">${esc(a.title)}</a>`
          : `<span>${esc(a.title)}</span>`)).join(', ')}</p>`
        : '';
      const shelf = this.shelfWords(card);
      const video = this.videoLink(card);
      const titleHTML = url && !this.kiosk ? `<a class="hmp-pick__link" href="${esc(url)}">${esc(card.title)}</a>` : esc(card.title);
      return `<article class="hmp-pick${wild ? ' hmp-pick--wild' : ''}${card.missingPieces ? ' hmp-pick--missing' : ''}" data-hmp-pick data-id="${esc(id)}" aria-labelledby="${this.uid}-p-${esc(id)}">
          <div class="hmp-pick__media">${img}
            ${wild ? `<span class="hmp-pick__wild">${this.icon('sparkle')}<span>${esc(this.t('results.wildcard'))}</span></span>` : ''}
            ${card.missingPieces ? `<span class="missing-stripe" aria-hidden="true">${esc(this.t('card.missing_stripe'))}</span>` : ''}
          </div>
          <div class="hmp-pick__head">
            ${badges.length ? `<p class="hmp-pick__badges">${badges.join('')}</p>` : ''}
            <h3 class="hmp-pick__title" id="${this.uid}-p-${esc(id)}">${titleHTML}</h3>
            ${this.statsHTML(card)}
          </div>
          <div class="hmp-pick__rest">
            <p class="gob-says hmp-pick__why"><span class="visually-hidden">${esc(this.t('card.says'))} </span>${esc(this.reasonLine(card))}</p>
            ${stand ? `<p class="hmp-pick__status" data-tone="${stand.tone}"><span class="hmp-pick__dot" aria-hidden="true"></span><span>${esc(stand.text)}</span></p>` : ''}
            ${notes.join('')}
            ${addOnsHTML}
            <div class="hmp-pick__extras">
              ${shelf ? `<details class="hmp-pick__shelf" data-hmp-shelf data-id="${esc(id)}"${this.shelfOpen() ? ' open' : ''}><summary class="hmp-pick__summary">${this.icon('map')}<span>${esc(this.t('shelf.find'))}</span></summary><p class="hmp-pick__shelf-words">${esc(shelf)}</p></details>` : ''}
              ${this.kiosk ? '' : `<a class="hmp-pick__video" href="${esc(video.href)}" target="_blank" rel="noopener" data-hmp-track="video" data-id="${esc(id)}">${this.icon('youtube')}<span>${esc(video.text)}</span><span class="visually-hidden"> ${esc(this.t('actions.new_tab'))}</span></a>`}
            </div>
            <div class="hmp-pick__actions" data-hmp-actions>${this.kiosk ? this.scanHTML(id) : this.actionsHTML(card)}</div>
            <p class="hmp-pick__said" data-hmp-said role="status">${this.notes[id] ? this.noteHTML(this.notes[id]) : ''}</p>
          </div>
        </article>`;
    }

    shelfOpen() {
      return this.kiosk || Boolean(this.state && !this.shared && this.state.context && this.state.context.door === 'table');
    }

    noteHTML(note) {
      const link = note.href ? ` <a class="text-link" href="${esc(note.href)}">${esc(note.link)}</a>` : '';
      return `<span class="hmp-pick__said-text${note.bad ? ' is-problem' : ''}">${esc(note.text)}</span>${link}`;
    }

    /** The kiosk: no logging in, so each pick's actions are a QR code of the shared picks to take away on a phone */
    scanHTML(id) {
      return `<div class="hmp-scan"><span class="hmp-scan__code" data-hmp-qr data-id="${esc(id)}"></span>
          <span class="hmp-scan__text"><span class="hmp-scan__title">${esc(this.t('keep.scan'))}</span><span class="hmp-scan__hint">${esc(this.t('keep.scan_hint'))}</span></span></div>`;
    }

    /* ---------- a pick's actions (contract v17, 9.3) ---------- */

    /** One action: a button that does it here (data-hmp-act) or a link that goes there (data-hmp-track) */
    act(kind, label, { id, href = '', track = '', style = 'ghost', icon = '' } = {}) {
      const cls = `button button--small${style === 'main' ? ' button--mana' : style === 'buy' ? ' button--gold' : ' button--ghost'} hmp-act`;
      const inner = `${icon ? this.icon(icon) : ''}<span>${esc(label)}</span>`;
      if (href) return `<a class="${cls}" href="${esc(href)}" data-hmp-track="${esc(track || kind)}" data-id="${esc(id)}">${inner}</a>`;
      return `<button class="${cls}" type="button" data-hmp-act="${esc(kind)}" data-id="${esc(id)}">${inner}</button>`;
    }

    /** The actions for a pick, the one that fits best first: in play mode, booking; borrowing, reserving; the
        roleplaying branch, a TTRPG session or the book. The rest sit under More. */
    actionsHTML(card) {
      const id = String(card.productId || '');
      const mode = this.modeOf();
      const plan = this.plan;
      const member = plan === 'member' || plan === 'unknown';
      const ctx = (this.state && !this.shared && this.state.context) || {};
      const stand = this.standing(card);
      const theirs = stand.tone === 'mine';
      const actions = [];
      const more = [];
      const login = this.loginUrl();
      const joinUrl = this.page('join');
      const book = () => this.act('book', this.t('actions.book'), { id, href: this.bookUrl(card), icon: 'table' });
      const reserve = (style) => {
        if (theirs) return this.act('lair', this.t('actions.my_lair'), { id, href: this.page('myLibrary'), track: 'more', style });
        if (!stand.free || (card.inStoreOnly && mode === 'borrow')) return '';
        if (plan === 'guest') return this.act('login', this.t('actions.login_reserve'), { id, href: login, track: 'reserve', style, icon: 'book' });
        if (plan === 'none') return this.act('join', this.t('actions.join'), { id, href: joinUrl, track: 'reserve', style, icon: 'book' });
        return this.act('reserve', this.t('actions.reserve'), { id, style, icon: 'book' });
      };
      const queue = () => {
        if (card.inStoreOnly || theirs) return '';
        if (plan === 'guest' || plan === 'none') return this.act('join', this.t('actions.join_queue'), { id, href: joinUrl, track: 'queue' });
        // already waiting in their queue: the button says so and goes to My Library, where the queue is
        if (this.queued.has(id)) return this.act('lair', this.t('actions.in_queue'), { id, href: this.page('myLibrary'), track: 'more', icon: 'check' });
        return this.act('queue', this.t('actions.queue'), { id, icon: 'list' });
      };
      const watch = () => {
        if (stand.free || theirs) return '';
        if (plan === 'guest') return this.act('login', this.t('actions.login_watch'), { id, href: login, track: 'watch' });
        return this.act('watch', this.t('actions.watch'), { id, icon: 'mail' });
      };
      const buy = () => (card.shopCopy && card.shopCopy.handle
        ? this.act('buy', card.shopCopy.price ? this.t('actions.buy_price', { price: this.money(card.shopCopy.price) }) : this.t('actions.buy'), { id, href: `/products/${encodeURIComponent(card.shopCopy.handle)}`, icon: 'pouch' })
        : '');

      if (mode === 'rpg') {
        const ttrpg = (style) => this.act('ttrpg', this.t('actions.ttrpg'), { id, href: this.page('gm'), track: 'book', style, icon: 'd20' });
        const bookPage = (style) => this.act('page', this.t('actions.book_page'), { id, href: this.pageUrl(card), track: 'more', style, icon: 'book' });
        const gmLair = !this.state || (this.state.rpg && this.state.rpg.gm) !== 'own';
        actions.push(gmLair ? ttrpg('main') : bookPage('main'), gmLair ? bookPage() : ttrpg());
        more.push(member ? reserve() : '', watch(), buy());
      } else if (mode === 'borrow') {
        if (card.inStoreOnly) actions.push(this.act('book', this.t('actions.book'), { id, href: this.bookUrl(card), style: 'main', icon: 'table' }));
        else actions.push(reserve('main') || watch(), queue());
        actions.push(card.inStoreOnly ? '' : book());
        more.push(buy());
      } else {
        // play here: at a table, booking a table, or adding it to the booking they've made
        if (ctx.bookingId) actions.push(this.act('booking', this.t('actions.add_booking'), { id, style: 'main', icon: 'check' }));
        else if (ctx.door !== 'table') actions.push(this.act('book', this.t('actions.book'), { id, href: this.bookUrl(card), style: 'main', icon: 'table' }));
        more.push(reserve(), queue(), watch(), buy());
      }
      const shown = actions.filter(Boolean);
      const rest = more.filter(Boolean);
      return `${shown.length ? `<div class="hmp-pick__buttons">${shown.join('')}</div>` : ''}${rest.length
        ? `<details class="hmp-more" data-hmp-more data-id="${esc(id)}"><summary class="hmp-more__summary">${esc(this.t('actions.more'))}${this.icon('chevron')}</summary><div class="hmp-more__list">${rest.join('')}</div></details>`
        : ''}`;
    }

    cardById(id) {
      const r = this.result() || {};
      return [...(r.picks || []), r.wildcard].find((c) => c && String(c.productId) === String(id)) || null;
    }

    /** After an action: its words under the card (read out), and the card's buttons drawn again for how it stands now */
    note(id, note, { redraw = true } = {}) {
      this.notes[id] = note;
      const article = this.querySelector(`[data-hmp-pick][data-id="${CSS.escape(String(id))}"]`);
      if (!article) return;
      const card = this.cardById(id);
      if (redraw && card) {
        const actions = article.querySelector('[data-hmp-actions]');
        if (actions) actions.innerHTML = this.actionsHTML(card);
        const status = article.querySelector('.hmp-pick__status');
        if (status) {
          const stand = this.standing(card);
          status.dataset.tone = stand.tone;
          status.lastElementChild.textContent = stand.text;
        }
      }
      const said = article.querySelector('[data-hmp-said]');
      if (said) said.innerHTML = this.noteHTML(note);
    }

    /** A refusal from the Lair: its own words, and a way on (log in, join the library, My Library) */
    refused(id, error, kind) {
      const status = Number(error && error.status) || 0;
      const note = { text: (status >= 400 && status < 500 && error.message) || this.t('actions.failed'), bad: true };
      if (status === 401) Object.assign(note, { href: this.loginUrl(), link: this.t(kind === 'reserve' ? 'actions.login_reserve' : 'keep.login_save') });
      else if (status === 403 && (kind === 'reserve' || kind === 'queue')) {
        this.lairPlan = null;
        Object.assign(note, { href: this.page('join'), link: this.t('actions.join') });
      } else if (status === 409 && kind === 'reserve') Object.assign(note, { href: this.page('myLibrary'), link: this.t('actions.my_lair') });
      this.note(id, note, { redraw: status === 403 });
    }

    async doAction(kind, id, button) {
      const card = this.cardById(id);
      if (!card || this.busy) return;
      this.busy = true;
      button.setAttribute('aria-busy', 'true');
      button.disabled = true;
      const done = () => {
        this.busy = false;
        if (button.isConnected) {
          button.removeAttribute('aria-busy');
          button.disabled = false;
        }
      };
      try {
        if (kind === 'reserve') {
          this.track('reserve', id);
          const data = await this.call('/library/holds', {
            body: { variantId: String(card.variantId || ''), productId: String(card.productId), title: card.title, shelfCode: card.shelfCode || '', handle: card.handle || '', image: card.image || null },
          });
          const hold = data && data.hold;
          const a = (card.availability = { ...(card.availability || {}) });
          a.available = Math.max(0, int(a.available) - 1);
          a.held = int(a.held) + 1;
          if (hold) a.mine = { id: hold.id, until: Number(hold.until) };
          done();
          this.persist();
          // the card's own line now says until when, and its button goes to My Library
          this.note(id, { text: `${this.t('actions.reserved')} ${this.t('actions.collect')}` });
        } else if (kind === 'queue') {
          this.track('queue', id);
          const data = await this.call('/me/queue', { body: { productId: String(card.productId) } });
          if (data && 'plan' in data) this.lairPlan = data.plan || null;
          this.knowQueue(data);
          const item = data && Array.isArray(data.queue) ? data.queue.find((q) => q && String(q.productId) === id) : null;
          const words = { title: card.title };
          let text;
          if (item && item.status === 'held' && item.hold) {
            // it was next and a copy was free, so the queue is holding it for them now (by post, until the postage cutoff)
            const a = (card.availability = { ...(card.availability || {}) });
            if (!(a.mine && a.mine.id)) {
              a.available = Math.max(0, int(a.available) - 1);
              a.held = int(a.held) + 1;
            }
            a.mine = { id: item.holdId || 'queue', until: Number(item.hold.until) };
            const post = data.settings && data.settings.delivery === 'post';
            text = post
              ? this.t('actions.queued_post', { ...words, when: this.when(item.hold.until) })
              : `${this.t('actions.queued_held', words)} ${this.t('actions.collect')}`;
            this.persist();
          } else if (data && data.added === false) text = this.t('actions.queued_already', words);
          else {
            // its place in the queue, numbered as My Library and the game's page number it
            const place = item ? int(item.position) : 0;
            text = place > 1 ? this.t('actions.queued_place', { ...words, place }) : this.t('actions.queued', words);
          }
          done();
          this.note(id, { text });
        } else if (kind === 'watch') {
          this.track('watch', id);
          await this.call('/library/watch', { body: { productId: String(card.productId) } });
          done();
          this.note(id, { text: this.t('actions.watching', { title: card.title }) }, { redraw: false });
        } else if (kind === 'booking') {
          const bookingId = this.state.context && this.state.context.bookingId;
          this.track('book', id);
          const data = await this.call(`/bookings/${encodeURIComponent(bookingId)}/game`, { body: { productId: String(card.productId) } });
          done();
          this.note(id, { text: this.t('actions.added_booking', { title: card.title }) }, { redraw: false });
          // the booking page shows the game on the booking (r17-member's lair-booking.js)
          document.dispatchEvent(new CustomEvent('help-me-pick:booking-game', { detail: { bookingId, productId: String(card.productId), title: card.title, image: card.image || null, handle: card.handle || '', booking: (data && data.booking) || null } }));
        }
      } catch (error) {
        done();
        this.refused(id, error, kind);
      }
    }

    /** The games waiting in their queue, from any /me/queue answer (a held one shows as theirs through its availability) */
    knowQueue(data) {
      if (!data || !Array.isArray(data.queue)) return;
      this.queued = new Set(data.queue.filter((q) => q && q.status === 'queued').map((q) => String(q.productId)));
    }

    /** Their library plan from the Lair, once a visit (GET /me/queue's plan), so the buttons say Reserve or Join */
    async askPlan() {
      if (!this.customer || this.planAsked) return;
      this.planAsked = true;
      try {
        const data = await this.call('/me/queue');
        if (data && 'plan' in data) {
          this.lairPlan = data.plan && int(data.plan.games) > 0 ? data.plan : null;
          this.knowQueue(data);
          this.querySelectorAll('[data-hmp-pick]').forEach((article) => {
            const card = this.cardById(article.dataset.id);
            const actions = article.querySelector('[data-hmp-actions]');
            if (card && actions && !this.kiosk) actions.innerHTML = this.actionsHTML(card);
          });
        }
      } catch {
        this.planAsked = false; // the tags' hint stands; the Lair's own refusal still says what to do
      }
    }

    /* ---------- fine-tune ---------- */

    tuneHTML() {
      const s = this.state;
      if (s.mode === 'rpg') return '';
      const f = s.filters;
      const open = this.tuneOpen ? ' open' : '';
      const check = (key, label) => `<label class="check hmp-tune__check"><input class="check__input" type="checkbox" data-hmp-filter="${key}"${f[key] ? ' checked' : ''}><span class="check__text">${esc(label)}</span></label>`;
      const loves = f.loves.map((x, i) => `<li><button class="hmp-love" type="button" data-hmp-love-remove="${i}" aria-label="${esc(this.t('tune.loves_remove', { title: x.title }))}"><span>${esc(x.title)}</span>${this.icon('close')}</button></li>`).join('');
      return `<details class="hmp-tune" data-hmp-tune${open}>
          <summary class="hmp-tune__summary"><span class="hmp-tune__title">${this.icon('filter')}<span>${esc(this.t('tune.heading'))}</span></span>${this.icon('chevron')}</summary>
          <div class="hmp-tune__body">
            <p class="hmp-tune__hint">${esc(this.t('tune.hint'))}</p>
            <fieldset class="hmp-tune__group">
              <legend class="hmp-tune__legend">${esc(this.t('tune.themes'))}</legend>
              <div class="hmp-tune__chips">${THEMES.map((g) => `<label class="chip"><input class="chip__input" type="checkbox" data-hmp-theme="${g}"${f.themes.includes(g) ? ' checked' : ''}><span class="chip__label">${esc(this.t(`tune.theme.${g}`))}</span></label>`).join('')}</div>
            </fieldset>
            <div class="hmp-tune__group hmp-loves" data-hmp-loves>
              <label class="hmp-tune__legend" for="${this.uid}-loves">${esc(this.t('tune.loves'))}</label>
              <p class="hmp-tune__small" id="${this.uid}-loves-hint">${esc(this.t('tune.loves_hint'))}</p>
              ${loves ? `<ul class="hmp-loves__chosen" role="list">${loves}</ul>` : ''}
              <div class="hmp-loves__search">
                <input class="input hmp-loves__input" id="${this.uid}-loves" type="search" autocomplete="off" spellcheck="false" maxlength="60"
                  placeholder="${esc(this.t('tune.loves_placeholder'))}" role="combobox" aria-autocomplete="list" aria-expanded="false"
                  aria-controls="${this.uid}-loves-list" aria-describedby="${this.uid}-loves-hint" data-hmp-loves-input${f.loves.length >= MAX_LOVES ? ' disabled' : ''}>
                <ul class="hmp-loves__list" id="${this.uid}-loves-list" role="listbox" aria-label="${esc(this.t('tune.loves'))}" data-hmp-loves-list hidden></ul>
              </div>
              <p class="hmp-tune__small" data-hmp-loves-say aria-live="polite">${f.loves.length >= MAX_LOVES ? esc(this.t('tune.loves_full')) : ''}</p>
            </div>
            <div class="hmp-tune__group hmp-tune__checks">
              ${check('lightReading', this.t('tune.light_reading'))}
              ${check('colourBlind', this.t('tune.colour_blind'))}
              ${check('easyPieces', this.t('tune.easy_pieces'))}
              ${this.customer ? check('hideHad', this.t('tune.hide_had')) : ''}
            </div>
            <p class="hmp-tune__status" data-hmp-tune-status aria-live="polite"></p>
          </div>
        </details>`;
    }

    /** Games you already love: GET /pick/known as they type (2 characters or more), a moment after the last key */
    searchLoves(q) {
      window.clearTimeout(this.lovesTimer);
      const list = this.querySelector('[data-hmp-loves-list]');
      const input = this.querySelector('[data-hmp-loves-input]');
      const text = String(q || '').trim();
      if (!list || !input) return;
      if (text.length < 2) {
        this.closeLoves();
        return;
      }
      this.lovesTimer = window.setTimeout(async () => {
        const ask = (this.lovesAsk = (this.lovesAsk || 0) + 1);
        try {
          const data = await this.call(`/pick/known?q=${encodeURIComponent(text.slice(0, 60))}`);
          if (ask !== this.lovesAsk) return;
          const chosen = new Set(this.state.filters.loves.map((x) => x.key));
          this.found = (Array.isArray(data.games) ? data.games : []).filter((g) => g && g.title && !chosen.has(g.key)).slice(0, 8);
          this.lovesAt = -1;
          this.drawLoves(text);
        } catch (error) {
          if (ask !== this.lovesAsk) return;
          this.found = [];
          this.closeLoves();
          const say = this.querySelector('[data-hmp-loves-say]');
          if (say) say.textContent = this.problem(error);
        }
      }, 250);
    }

    drawLoves(q) {
      const list = this.querySelector('[data-hmp-loves-list]');
      const input = this.querySelector('[data-hmp-loves-input]');
      const say = this.querySelector('[data-hmp-loves-say]');
      if (!list || !input) return;
      const found = this.found || [];
      list.innerHTML = found.map((g, i) => `<li class="hmp-loves__option" role="option" id="${this.uid}-love-${i}" data-hmp-love="${i}" aria-selected="${i === this.lovesAt ? 'true' : 'false'}">${esc(g.title)}</li>`).join('');
      list.hidden = !found.length;
      input.setAttribute('aria-expanded', found.length ? 'true' : 'false');
      if (this.lovesAt >= 0) input.setAttribute('aria-activedescendant', `${this.uid}-love-${this.lovesAt}`);
      else input.removeAttribute('aria-activedescendant');
      if (say) say.textContent = found.length ? this.t('tune.loves_found', { count: found.length }) : this.t('tune.loves_none', { q });
    }

    closeLoves() {
      const list = this.querySelector('[data-hmp-loves-list]');
      const input = this.querySelector('[data-hmp-loves-input]');
      if (list) {
        list.hidden = true;
        list.innerHTML = '';
      }
      if (input) {
        input.setAttribute('aria-expanded', 'false');
        input.removeAttribute('aria-activedescendant');
      }
      this.found = [];
      this.lovesAt = -1;
    }

    addLove(i) {
      const g = (this.found || [])[i];
      const f = this.state.filters;
      if (!g || f.loves.length >= MAX_LOVES) return;
      const known = String(g.key || '').startsWith('known:');
      f.loves.push({ key: String(g.key || g.productId || g.title), title: g.title, productId: !known && g.productId ? String(g.productId) : null });
      this.redrawTune({ focus: '[data-hmp-loves-input]' });
      this.tune();
    }

    removeLove(i) {
      const f = this.state.filters;
      f.loves.splice(i, 1);
      this.redrawTune({ focus: f.loves.length ? `[data-hmp-love-remove="${Math.min(i, f.loves.length - 1)}"]` : '[data-hmp-loves-input]' });
      this.tune();
    }

    /** The fine-tune box drawn again where it is (open), focus put back where they were */
    redrawTune({ focus = null } = {}) {
      const box = this.querySelector('[data-hmp-tune]');
      if (!box) return;
      this.tuneOpen = true;
      const wrap = document.createElement('div');
      wrap.innerHTML = this.tuneHTML();
      const fresh = wrap.firstElementChild;
      box.replaceWith(fresh);
      if (focus) fresh.querySelector(focus)?.focus({ preventScroll: true });
    }

    /* ---------- keeping the picks: save, share, email ---------- */

    keepHTML() {
      if (this.kiosk) return '';
      const saveButton = this.customer
        ? `<button class="button button--ghost" type="button" data-hmp-save>${this.icon('heart')}<span>${esc(this.t('keep.save'))}</span></button>`
        : `<a class="button button--ghost" href="${esc(this.loginUrl())}" data-hmp-track="save">${this.icon('heart')}<span>${esc(this.t('keep.login_save'))}</span></a>`;
      const email = (this.cfg.customer && this.cfg.customer.email) || '';
      return `<section class="hmp-keep" aria-labelledby="${this.uid}-keep">
          <h3 class="hmp-keep__heading" id="${this.uid}-keep">${esc(this.t('keep.heading'))}</h3>
          <div class="hmp-keep__row">
            ${saveButton}
            <button class="button button--ghost" type="button" data-hmp-share>${this.icon('external')}<span>${esc(this.t('keep.share'))}</span></button>
            <button class="button button--ghost" type="button" data-hmp-email-open aria-expanded="false" aria-controls="${this.uid}-email">${this.icon('mail')}<span>${esc(this.t('keep.email'))}</span></button>
          </div>
          <div class="hmp-keep__copy" data-hmp-copy hidden>
            <label class="field__label" for="${this.uid}-link">${esc(this.t('keep.copy_hint'))}</label>
            <input class="input" id="${this.uid}-link" type="text" readonly data-hmp-link>
          </div>
          <form class="hmp-keep__email" id="${this.uid}-email" data-hmp-email novalidate hidden>
            <div class="field">
              <label class="field__label" for="${this.uid}-email-input">${esc(this.t('keep.email_label'))}</label>
              <input class="input" id="${this.uid}-email-input" type="email" name="email" autocomplete="email" required value="${esc(email)}" aria-describedby="${this.uid}-email-error">
              <p class="field__error" id="${this.uid}-email-error" data-hmp-email-error hidden></p>
            </div>
            <label class="check"><input class="check__input" type="checkbox" name="newsletter"><span class="check__text">${esc(this.t('keep.newsletter'))}</span></label>
            <button class="button button--mana" type="submit">${esc(this.t('keep.send'))}</button>
          </form>
          <p class="hmp-keep__said" data-hmp-keep-said role="status"></p>
        </section>`;
    }

    keepSay(text, bad = false) {
      const box = this.querySelector('[data-hmp-keep-said]');
      if (!box) return;
      box.innerHTML = `<span class="hmp-pick__said-text${bad ? ' is-problem' : ''}">${esc(text)}</span>`;
    }

    async save(button) {
      const s = this.state;
      if (!s.session || this.busy) return;
      this.busy = true;
      button.setAttribute('aria-busy', 'true');
      try {
        await this.call(`/pick/${encodeURIComponent(s.session.id)}/save`, { body: { editKey: s.session.editKey } });
        this.track('save');
        this.keepSay(this.t('keep.saved'));
      } catch (error) {
        if (error.status === 401) window.location.href = this.loginUrl();
        else this.keepSay(this.problem(error), true);
      } finally {
        this.busy = false;
        button.removeAttribute('aria-busy');
      }
    }

    /** The phone's own share sheet when there is one, else the link to copy (copied straight away where allowed) */
    async share({ type = false } = {}) {
      const session = (this.shared && this.shared.session) || this.state.session;
      if (!session) return;
      const url = this.shareUrl(session.id);
      const r = this.result() || {};
      const text = type && r.playerType ? this.t('type.share_text', { type: this.t(`type.${r.playerType}`) }) : this.t('keep.share_text');
      this.track('share');
      if (navigator.share) {
        try {
          await navigator.share({ title: this.t('keep.share_title'), text, url });
          return;
        } catch (error) {
          if (error && error.name === 'AbortError') return;
        }
      }
      const box = this.querySelector('[data-hmp-copy]');
      const input = this.querySelector('[data-hmp-link]');
      if (box && input) {
        box.hidden = false;
        input.value = url;
      }
      let copied = false;
      try {
        await navigator.clipboard.writeText(url);
        copied = true;
      } catch {
        copied = false;
      }
      if (copied) this.keepSay(this.t('keep.copied'));
      if (input) {
        input.focus({ preventScroll: false });
        input.select();
      }
    }

    async email(form) {
      const s = this.state;
      const input = form.querySelector('input[name="email"]');
      const error = form.querySelector('[data-hmp-email-error]');
      const address = String(input.value || '').trim();
      const showError = (text) => {
        error.textContent = text;
        error.hidden = !text;
        input.setAttribute('aria-invalid', text ? 'true' : 'false');
        if (text) input.focus();
      };
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(address)) {
        showError(this.t('keep.email_invalid'));
        return;
      }
      showError('');
      if (!s.session || this.busy) return;
      const button = form.querySelector('button[type="submit"]');
      this.busy = true;
      button.setAttribute('aria-busy', 'true');
      try {
        await this.call(`/pick/${encodeURIComponent(s.session.id)}/email`, { body: { email: address, editKey: s.session.editKey } });
        this.track('email');
        if (form.querySelector('input[name="newsletter"]').checked) this.newsletter(address);
        form.hidden = true;
        this.querySelector('[data-hmp-email-open]')?.setAttribute('aria-expanded', 'false');
        this.keepSay(this.t('keep.sent', { email: address }));
        this.querySelector('[data-hmp-email-open]')?.focus({ preventScroll: true });
      } catch (err) {
        if (err.status === 422) showError(err.message || this.t('keep.email_invalid'));
        else this.keepSay(this.problem(err), true);
      } finally {
        this.busy = false;
        button.removeAttribute('aria-busy');
      }
    }

    /** The newsletter tick: Shopify's own customer form (contract v17, 3.9), sent quietly in the background */
    newsletter(address) {
      const body = new URLSearchParams({ form_type: 'customer', utf8: '✓', 'contact[email]': address, 'contact[tags]': 'newsletter,help-me-pick' });
      fetch('/contact', { method: 'POST', body, credentials: 'same-origin', headers: { Accept: 'text/html' } }).catch(() => {});
    }

    /* ---------- shared picks (?p=) ---------- */

    async loadShared(id) {
      this.shared = { id, loading: true, session: { id }, result: null, mode: 'play' };
      this.render();
      try {
        const data = await this.call(`/pick/${encodeURIComponent(id)}`);
        if (!this.shared || this.shared.id !== id) return;
        const mode = data.mode || 'play';
        this.shared = {
          id,
          session: { id: String((data.session && data.session.id) || id) },
          mode,
          result: { mode, picks: Array.isArray(data.picks) ? data.picks.filter(Boolean) : [], wildcard: data.wildcard || null, playerType: data.playerType || null, relaxed: [] },
        };
        this.render();
        this.say(this.announceText());
      } catch (error) {
        if (!this.shared || this.shared.id !== id) return;
        this.shared = { id, error, session: null, result: null, mode: 'play' };
        this.render();
        this.say(error.status === 404 ? this.t('results.gone') : this.problem(error));
      }
    }

    renderShared() {
      const sh = this.shared;
      const take = `<button class="button button--mana" type="button" data-hmp-take-quiz>${esc(this.t('results.take_quiz'))}</button>`;
      let body;
      if (sh.loading) body = this.waitHTML(this.t('loading_shared'));
      else if (sh.error && sh.error.status === 404) {
        body = `<div class="hmp-error"><p class="hmp-error__text" tabindex="-1" data-hmp-focus>${esc(this.t('results.gone'))}</p>${take}</div>`;
      } else if (sh.error) {
        this.retry = () => this.loadShared(sh.id);
        body = this.errorHTML(this.problem(sh.error));
      } else {
        body = `<section class="hmp-results hmp-results--shared" aria-labelledby="${this.uid}-results">
            <h2 class="hmp-results__heading" id="${this.uid}-results" tabindex="-1" data-hmp-focus>${esc(this.t('results.shared_heading'))}</h2>
            <p class="hmp-results__lede">${esc(this.t('results.shared_text'))}</p>
            <div data-hmp-picks-box>${this.picksHTML()}</div>
            <div class="hmp-results__again">${take}</div>
          </section>`;
      }
      this.root.innerHTML = `<div class="hmp__body" data-hmp-body>${body}</div>`;
      this.root.dataset.step = 'shared';
      if (!sh.loading && !sh.error) this.afterResults();
    }

    leaveShared() {
      this.shared = null;
      try {
        const u = new URL(window.location.href);
        u.searchParams.delete('p');
        history.replaceState(history.state, '', `${u.pathname}${u.search}${u.hash}`);
      } catch {
        /* the address stays */
      }
    }

    /* ---------- after drawing ---------- */

    afterResults() {
      if (this.kiosk) this.drawQR();
      else this.askPlan();
    }

    /** The kiosk's QR codes (assets/qr.js, loaded the first time one's needed) */
    drawQR() {
      const session = (this.shared && this.shared.session) || (this.state && this.state.session);
      const boxes = this.querySelectorAll('[data-hmp-qr]');
      if (!boxes.length || !session) return;
      if (!window.DGQR) {
        if (this.qrLoading || !this.cfg.qr) return;
        this.qrLoading = true;
        const script = document.createElement('script');
        script.src = this.cfg.qr;
        script.onload = () => this.drawQR();
        document.head.append(script);
        return;
      }
      const svg = window.DGQR.svg(this.shareUrl(session.id), { size: 128, margin: 3, label: this.t('keep.scan') });
      boxes.forEach((box) => {
        box.innerHTML = svg;
      });
    }

    /** Start again with the same door (the table, the booking): every answer goes */
    startOver({ quiet = false } = {}) {
      const s = this.state;
      const c = (s && s.context) || {};
      this.ask = (this.ask || 0) + 1;
      window.clearTimeout(this.tuneTimer);
      this.tuneOpen = false;
      if (this.shared) this.leaveShared();
      this.begin({ door: c.door || (this.inline ? 'page' : 'library'), table: c.table || null, bookingId: c.bookingId || null, date: c.date || null, tables: c.tables || null, players: null, mode: s && s.modeFixed ? s.mode : null });
      if (this.kiosk) storage.clear();
      this.render();
      if (quiet && this.inline) window.scrollTo(0, 0);
      this.focusTop();
    }

    /* ---------- events ---------- */

    onClick(event) {
      const on = (selector) => (event.target.closest ? event.target.closest(selector) : null);
      const s = this.state;
      let el;
      if (on('[data-hmp-still]')) this.stay();
      else if (on('[data-hmp-close]')) this.close();
      else if (on('[data-hmp-back]')) {
        if (s.step === 'swipe' && !this.view && this.unswipe()) return;
        this.back();
      } else if ((el = on('[data-hmp-answer]'))) this.answer(el.dataset.hmpAnswer, el.dataset.value);
      else if (on('[data-hmp-rpg]')) {
        s.mode = 'rpg';
        this.next();
      } else if ((el = on('[data-hmp-vibe]'))) {
        const v = el.dataset.hmpVibe;
        s.vibes = s.vibes.includes(v) ? s.vibes.filter((x) => x !== v) : [...s.vibes, v];
        el.setAttribute('aria-pressed', s.vibes.includes(v) ? 'true' : 'false');
        const next = this.querySelector('[data-hmp-next]');
        if (next) next.hidden = !s.vibes.length;
        this.persist();
      } else if (on('[data-hmp-next]')) this.next();
      else if (on('[data-hmp-any]')) {
        s.vibes = [];
        this.next();
      } else if ((el = on('[data-hmp-age]'))) this.setAge((int(s.youngest) || 6) + int(el.dataset.hmpAge));
      else if ((el = on('[data-hmp-swipe-answer]'))) this.swipe(el.dataset.hmpSwipeAnswer, { from: 'button' });
      else if (on('[data-hmp-just-show]')) {
        s.swipeSkipped = true;
        this.next();
      } else if (on('[data-hmp-taste]')) this.next();
      else if (on('[data-hmp-retry]')) {
        const retry = this.retry;
        this.view = null;
        if (retry) retry();
        else this.render();
      } else if (on('[data-hmp-again]')) {
        this.track('again');
        this.fetchPicks({ again: true });
      } else if (on('[data-hmp-start-again]')) this.startOver();
      else if (on('[data-hmp-take-quiz]')) {
        this.leaveShared();
        this.begin(this.contextFrom({ door: 'share' }));
        this.render();
        this.focusTop();
      } else if ((el = on('[data-hmp-act]'))) this.doAction(el.dataset.hmpAct, el.dataset.id, el);
      else if ((el = on('[data-hmp-track]'))) this.track(el.dataset.hmpTrack, el.dataset.id && el.dataset.id !== 'all' ? el.dataset.id : null);
      else if ((el = on('[data-hmp-save]'))) this.save(el);
      else if (on('[data-hmp-share-type]')) this.share({ type: true });
      else if (on('[data-hmp-share]')) this.share();
      else if ((el = on('[data-hmp-email-open]'))) {
        const form = this.querySelector('[data-hmp-email]');
        if (!form) return;
        form.hidden = !form.hidden;
        el.setAttribute('aria-expanded', form.hidden ? 'false' : 'true');
        if (!form.hidden) form.querySelector('input[name="email"]').focus();
      } else if ((el = on('[data-hmp-love]'))) this.addLove(int(el.dataset.hmpLove));
      else if ((el = on('[data-hmp-love-remove]'))) this.removeLove(int(el.dataset.hmpLoveRemove));
      else if (on('[data-hmp-tune-retry]')) this.fetchPicks({ tune: true });
    }

    setAge(value) {
      const s = this.state;
      s.youngest = clamp(int(value) || 6, 2, 17);
      const input = this.querySelector('[data-hmp-age-input]');
      if (input) input.value = String(s.youngest);
      this.querySelectorAll('[data-hmp-age]').forEach((b) => {
        b.disabled = (int(b.dataset.hmpAge) < 0 && s.youngest <= 2) || (int(b.dataset.hmpAge) > 0 && s.youngest >= 17);
      });
      this.persist();
    }

    onChange(event) {
      const t = event.target;
      const s = this.state;
      if (!s) return;
      if (t.matches('[data-hmp-kids]')) {
        s.kids = t.checked;
        const box = this.querySelector('[data-hmp-age-box]');
        if (box) box.hidden = !s.kids;
        this.persist();
      } else if (t.matches('[data-hmp-age-input]')) this.setAge(t.value);
      else if (t.matches('[data-hmp-theme]')) {
        const g = t.dataset.hmpTheme;
        const f = s.filters;
        f.themes = t.checked ? [...new Set([...f.themes, g])] : f.themes.filter((x) => x !== g);
        this.tune();
      } else if (t.matches('[data-hmp-filter]')) {
        s.filters[t.dataset.hmpFilter] = t.checked;
        this.tune();
      }
    }

    onInput(event) {
      if (event.target.matches('[data-hmp-loves-input]')) this.searchLoves(event.target.value);
    }

    onKey(event) {
      const t = event.target;
      if (t.matches && t.matches('[data-hmp-card]')) {
        const keys = { ArrowLeft: 'nope', ArrowRight: 'like', ArrowDown: 'skip' };
        if (keys[event.key]) {
          event.preventDefault();
          this.swipe(keys[event.key], { from: 'key' });
        }
        return;
      }
      if (t.matches && t.matches('[data-hmp-loves-input]')) {
        const found = this.found || [];
        if ((event.key === 'ArrowDown' || event.key === 'ArrowUp') && found.length) {
          event.preventDefault();
          const step = event.key === 'ArrowDown' ? 1 : -1;
          this.lovesAt = ((this.lovesAt ?? -1) + step + found.length) % found.length;
          this.drawLoves(t.value);
        } else if (event.key === 'Enter') {
          event.preventDefault();
          if (this.lovesAt >= 0) this.addLove(this.lovesAt);
          else if (found.length === 1) this.addLove(0);
        } else if (event.key === 'Escape' && found.length) {
          event.preventDefault();
          event.stopPropagation();
          this.closeLoves();
        }
      }
    }

    onSubmit(event) {
      if (!event.target.matches('[data-hmp-email]')) return;
      event.preventDefault();
      this.email(event.target);
    }

    onToggle(event) {
      const t = event.target;
      if (!(t instanceof HTMLDetailsElement)) return;
      if (t.matches('[data-hmp-tune]')) this.tuneOpen = t.open;
      else if (t.open && t.matches('[data-hmp-shelf]')) this.track('shelf', t.dataset.id);
      else if (t.open && t.matches('[data-hmp-more]')) this.track('more', t.dataset.id);
    }
  }

  customElements.define('help-me-pick', HelpMePick);

  /* Any [data-help-me-pick] opens the quiz (data-door, data-mode). A link (the banners) still goes to the quiz's own
     page when there's no quiz here to open, or before this script has run. */
  document.addEventListener('click', (event) => {
    const opener = event.target.closest ? event.target.closest('[data-help-me-pick]') : null;
    if (!opener || event.defaultPrevented || event.metaKey || event.ctrlKey || event.shiftKey) return;
    const el = hostEl();
    if (!el || !el.ready) return;
    event.preventDefault();
    el.open({ door: opener.dataset.door, mode: opener.dataset.mode, table: opener.dataset.table }, opener);
  });

  /* The booking page's "Pick a game for your table": { door, mode, players, date, bookingId, tables } */
  document.addEventListener('help-me-pick:open', (event) => {
    const el = hostEl();
    if (el && el.ready) el.open(event.detail || {}, document.activeElement);
  });

  /* Back from logging in (?hmp=open): the quiz opens again on their picks */
  try {
    const u = new URL(window.location.href);
    if (u.searchParams.get('hmp') === 'open') {
      u.searchParams.delete('hmp');
      history.replaceState(history.state, '', `${u.pathname}${u.search}${u.hash}`);
      const el = hostEl();
      if (el && el.ready && !el.inline) el.open({}, null);
    }
  } catch {
    /* fine */
  }
})();
